import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { useAuth } from '../../components/auth/AuthContext';
import { TradeType, Direction, FixingReference, FixingOffset, SettlementType, Shortcut, AllocationRow } from '../../types/tradeBooking';
import { useTradeBookingForm } from '../../hooks/useTradeBookingForm';
import { saveDraft, submitTrade, updateTrade, bookDraft, listMultiTradeDrafts, PotentialConfirmationMatch } from '../../services/tradeBookingService';
import { MultiTradeDraftSummary } from '../../types/tradeBooking';
import { getTradeBookingShortcuts, saveTradeBookingShortcuts, getUserPreferences, saveDateConventionOverrides, saveFixingExpiryConventionOverrides, DateConventionOverride, FixingExpiryConventionOverride } from '../../services/userPreferencesService';
import { getDateConventions, getFixingExpiryConventions, DateConvention as MarketDateConvention, FixingExpiryConvention as MarketFixingExpiryConvention } from '../../services/marketConventionsService';
import { startRecording, transcribeAudio, AudioRecorder } from '../../services/speechService';
import { getEnabledCurrencies, getValidCurrency2Options, getCurrency1Options, getSettlementCurrencyOptions } from '../../types/currency';
import { formatAmountDisplay, formatPriceDisplay, parseAmountInput } from '../../utils/amountFormatter';
import { parseDateFromInput, calculateDaysDifference, addDays, parseTradeDate } from '../../utils/dateCalculations';
import { formatMaturity } from '../../utils/maturityParser';
import { businessCalendar } from '../../services/businessCalendar';
import { validateNotes, validateHedgeObject } from '../../utils/inputValidation';
import { bankService, Bank } from '../../services/api/bankService';
import { clientService, TradingEntity } from '../../services/api/clientService';
import { groupingsService } from '../../services/api/groupingsService';
import { GroupingsConfig } from '../../types/groupings';
import ConfirmationDialog from '../../components/tradeBooking/ConfirmationDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import FormActions from '../../components/tradeBooking/FormActions';
import ShortcutsModal from '../../components/tradeBooking/ShortcutsModal';
import DatesSettingsModal, { DateConvention } from '../../components/tradeBooking/DatesSettingsModal';
import FixingExpirySettingsModal, { FixingExpiryConvention } from '../../components/tradeBooking/FixingExpirySettingsModal';
import Toast from '../../components/common/Toast';
import ResizeHandle from '../../components/common/ResizeHandle';
import ClientTradesGrid from '../../components/grids/ClientTradesGrid';
import { Quote } from '../../types/quote';
import QuoteButton from '../../components/tradeBooking/QuoteButton';
import QuoteTimer from '../../components/tradeBooking/QuoteTimer';
import MultiQuoteModal from '../../components/tradeBooking/MultiQuoteModal';
import AllocationModal from '../../components/tradeBooking/AllocationPanel';
import FixingSchedulePanel from '../../components/tradeBooking/FixingSchedulePanel';
import { generateForwardMailto } from '../../utils/forwardMailto';
import { Mail, Bell, Layers } from 'lucide-react';
import { clientService as mainClientService } from '../../services/clientService';
import FxgoCorrectionsModal from '../../components/fxgo/FxgoCorrectionsModal';
import { useEventStreamSimple } from '../../hooks/useEventStreamSimple';
import TradeImageCapturePanel from '../../components/tradeBooking/TradeImageCapturePanel';
import MultiTradeModal from '../../components/tradeBooking/MultiTradeModal';
import { TradeImageCaptureResult, MultiTradeExtractionResult } from '../../types/tradeImageCapture';
import { tradeImageCaptureService } from '../../services/api/tradeImageCaptureService';
import BusinessDayDatePicker from '../../components/tradeBooking/BusinessDayDatePicker';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSystemDate } from '../../hooks/useSystemDate';
import { workflowService } from '../../services/api/workflowService';
import type { ApprovalRequirements } from '../../types/workflow';
import { quoteApprovalService } from '../../services/api/quoteApprovalService';
import type { QuoteApproval } from '../../types/quoteApproval';
import '../../styles/TradeBooking.css';
import '../../styles/tradeImageCapture.css';

const TradeBooking: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language;
  const currencies = getEnabledCurrencies();

  const isSupervisor = Boolean(user?.role?.includes('admin') || user?.role?.includes('supervisor'));
  const isReadOnlyRole = user?.role === 'client_ro_user' || user?.role === 'client_acc_user';

  // Date range filter — defaults to last 2 business days based on systemDate
  const clientId = user?.organization?.id || user?.id;
  const systemDateResult = useSystemDate(clientId);
  const { systemDate, previousBusinessDay } = systemDateResult;
  const [dateFrom, setDateFrom] = React.useState<string | null>(previousBusinessDay || null);
  const [dateTo, setDateTo] = React.useState<string | null>(systemDate || null);
  const dateDefaultsSet = React.useRef(false);

  React.useEffect(() => {
    if (systemDate && previousBusinessDay && !dateDefaultsSet.current) {
      dateDefaultsSet.current = true;
      setDateFrom(previousBusinessDay);
      setDateTo(systemDate);
    }
  }, [systemDate, previousBusinessDay]);

  // Load workflow config to check demo mode and approval requirements
  useEffect(() => {
    const loadWorkflowConfig = async () => {
      if (!clientId) return;
      try {
        const response = await workflowService.getWorkflowConfig(clientId);
        if (response.success && response.data) {
          setIsDemoMode(response.data.demoMode || false);
          setApprovalRequirements(response.data.approvalRequirements || null);
        }
      } catch {
        // Silently fail - demo mode defaults to false, approvals default to 0
      }
    };
    loadWorkflowConfig();
  }, [clientId]);

  const [dateConventions, setDateConventions] = useState<DateConvention[]>([]);

  const {
    form,
    errors,
    touched,
    updateField,
    bulkUpdateFields,
    handleTradeTypeChange,
    handleMaturityChange,
    handleExpiryDateChange,
    handleValueDateChange,
    handleSettlementTypeChange,
    handleAverageRateForwardToggle,
    handleForwardStartingToggle,
    handleFixingScheduleAddDates,
    handleFixingScheduleRemoveDate,
    handleFixingScheduleClearAll,
    handleForwardStartingScheduleAddDates,
    handleForwardStartingScheduleRemoveDate,
    handleForwardStartingScheduleClearAll,
    touchField,
    validate,
    resetForm,
    // Trade Image Capture (KAN-82)
    captureConfidence,
    captureRawText,
    setCaptureConfidenceValues,
    clearAllConfidence,
  } = useTradeBookingForm({ systemDate, dateConventions });

  // Local input states for formatted amounts
  const [amountInput, setAmountInput] = useState<string>('');
  const [priceInput, setPriceInput] = useState<string>('');
  const [spotPriceInput, setSpotPriceInput] = useState<string>('');
  const [forwardPointsInput, setForwardPointsInput] = useState<string>('');

  const [isLoading, setIsLoading] = useState(false);

  // Per-section loading states
  const [loadingShortcuts, setLoadingShortcuts] = useState(true);
  const [loadingCoreData, setLoadingCoreData] = useState(true);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingFixing, setLoadingFixing] = useState(true);
  const [loadingClassification, setLoadingClassification] = useState(true);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState<'draft' | 'submit' | 'approval' | 'update' | 'book_draft'>('submit');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [notesError, setNotesError] = useState<string | null>(null);
  const [hedgeObjectError, setHedgeObjectError] = useState<string | null>(null);

  // Demo mode (gates quote functionality)
  const [isDemoMode, setIsDemoMode] = useState(false);
  // Workflow approval requirements (KAN-201: determines button labels)
  const [approvalRequirements, setApprovalRequirements] = useState<ApprovalRequirements | null>(null);

  // KAN-201: Determine if user can directly book or must request approval
  // Based on workflow config approval count, not just user role
  const canDirectlyBook = (() => {
    if (isSupervisor) return true; // Admins can always book directly
    if (!approvalRequirements) return true; // No config loaded = default 0 approvals = direct book
    const productKey = form.tradeType === TradeType.FORWARD ? 'forward' : 'spot';
    const count = approvalRequirements[productKey]?.draft_to_booked ?? 0;
    return count === 0;
  })();

  // Trade Image Capture state (KAN-82)
  const [isExtracting, setIsExtracting] = useState(false);
  const [captureResetTrigger, setCaptureResetTrigger] = useState(0);

  // Multi-Trade Modal state (KAN-125)
  const [multiTradeModalOpen, setMultiTradeModalOpen] = useState(false);
  const [multiTradeResult, setMultiTradeResult] = useState<MultiTradeExtractionResult | null>(null);

  // KAN-216: Pending confirmation match suggestion state
  const [pendingConfirmationMatch, setPendingConfirmationMatch] = useState<PotentialConfirmationMatch | null>(null);
  const [isReprocessing, setIsReprocessing] = useState(false);

  // Draft picker state — right-click on Layers button (KAN-187e)
  const [draftPickerOpen, setDraftPickerOpen] = useState(false);
  const [draftPickerPos, setDraftPickerPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [draftPickerDrafts, setDraftPickerDrafts] = useState<MultiTradeDraftSummary[]>([]);
  const [draftPickerLoading, setDraftPickerLoading] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);

  // Confidence highlighting helpers (KAN-82)
  const hasExtraction = Object.keys(captureConfidence).length > 0;
  const MANDATORY_EXTRACTABLE_FIELDS = [
    'tradeType', 'direction', 'amount', 'currency1', 'currency2', 'price',
    'tradeDate', 'expiryDate', 'counterparty', 'ourParty', 'settlementType',
  ];
  const getConfidenceClass = (fieldName: string): string => {
    const score = captureConfidence[fieldName];
    if (score === undefined) return '';
    if (score >= 0.9) return 'capture-high';
    if (score >= 0.6) return 'capture-medium';
    return 'capture-low';
  };
  const renderConfidenceIndicator = (fieldName: string) => {
    if (!hasExtraction) return null;
    const score = captureConfidence[fieldName];
    if (score !== undefined && score < 0.6) {
      const raw = captureRawText[fieldName];
      return (
        <span className="capture-low-tooltip">
          {' \u26A0'}
          {raw && <span className="capture-tooltip-text">{raw}</span>}
        </span>
      );
    }
    if (score === undefined && MANDATORY_EXTRACTABLE_FIELDS.includes(fieldName)) {
      return <span className="capture-not-found-badge">{t('tradeBooking.imageCapture.notFound')}</span>;
    }
    return null;
  };

  // Settings modals
  const [showShortcutsSettings, setShowShortcutsSettings] = useState(false);
  const [showDatesSettings, setShowDatesSettings] = useState(false);
  const [showFixingSettings, setShowFixingSettings] = useState(false);

  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [tradingEntities, setTradingEntities] = useState<TradingEntity[]>([]);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);
  const [allocationRows, setAllocationRows] = useState<AllocationRow[]>([]);
  const [showAllocationModal, setShowAllocationModal] = useState(false);

  // Force re-render when business calendar loads asynchronously
  const [, setCalendarVersion] = useState(0);

  // FXGO corrections state
  const [showFxgoCorrectionsModal, setShowFxgoCorrectionsModal] = useState(false);
  const [fxgoCorrectionsCount, setFxgoCorrectionsCount] = useState(0);

  const [editingShortcut, setEditingShortcut] = useState<Shortcut | null>(null);
  const [isNewShortcut, setIsNewShortcut] = useState(false);

  // Microphone states for each shortcut: 'idle' | 'listening' | 'processing' | 'success' | 'error'
  const [micStates, setMicStates] = useState<{[key: string]: string}>({});
  const [activeRecorders, setActiveRecorders] = useState<{[key: string]: AudioRecorder}>({});
  const [missedFields, setMissedFields] = useState<string[]>([]); // Fields that were empty but not extracted

  // Microphone states for each section
  const [sectionMicStates, setSectionMicStates] = useState<{[key: string]: string}>({
    basicData: 'idle',
    dates: 'idle',
    fixing: 'idle',
    notes: 'idle',
  });
  const [sectionRecorders, setSectionRecorders] = useState<{[key: string]: AudioRecorder}>({});

  // Any mic is active (listening or processing) — disables other mic buttons and actions
  const isVoiceActive = Object.values(sectionMicStates).some(s => s === 'listening' || s === 'processing')
    || Object.values(micStates).some(s => s === 'listening' || s === 'processing');
  // Audio is being sent to API — applies form overlay (NOT during listening, to avoid
  // breaking press-and-hold: the CSS repaint would fire onMouseLeave on the held button)
  const isVoiceProcessing = Object.values(sectionMicStates).some(s => s === 'processing')
    || Object.values(micStates).some(s => s === 'processing');

  // Store original conventions before editing (for cancel functionality)
  const [savedDateConventions, setSavedDateConventions] = useState<DateConvention[]>([]);

  // Track if dates have been manually modified (to prevent auto-population from overwriting)
  const [datesManuallyModified, setDatesManuallyModified] = useState(false);

  // Fixing & Expiry Conventions - Forward only
  const [fixingExpiryConventions, setFixingExpiryConventions] = useState<FixingExpiryConvention[]>([]);

  // Grid refresh and selection state
  const [gridRefreshTrigger, setGridRefreshTrigger] = useState(0);
  const [newlyCreatedTradeId, setNewlyCreatedTradeId] = useState<string | null>(null);
  const [gridClearSelectionTrigger, setGridClearSelectionTrigger] = useState(0);

  // View/edit mode: when a trade is selected from the grid
  const [isViewingTrade, setIsViewingTrade] = useState(false);
  const [editingTradeId, setEditingTradeId] = useState<string | null>(null);
  const [editingTradeClientId, setEditingTradeClientId] = useState<string | null>(null);
  const [editingWorkflowState, setEditingWorkflowState] = useState<string | null>(null);
  const [editingQuoteApproval, setEditingQuoteApproval] = useState<QuoteApproval | null>(null);

  // Quote state
  const [activeQuote, setActiveQuote] = useState<Quote | null>(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  const showNotification = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  // KAN-216: Handle reprocessing a confirmation match suggestion
  const handleReprocessConfirmation = async () => {
    if (!pendingConfirmationMatch || pendingConfirmationMatch.type !== 'single') return;
    const match = pendingConfirmationMatch;
    const tradingEntityId = form.ourPartyId || user?.organization?.id || user?.id;
    if (!tradingEntityId || !match.emailId) return;

    setIsReprocessing(true);
    try {
      await mainClientService.reprocessEmail(tradingEntityId, match.emailId, match.tradeIndex);
      showNotification(t('tradeBooking.messages.confirmationReprocessSuccess'), 'success');

      // KAN-260: Dismiss companion provisionals after successful definitive match
      if (match.companionProvisionals?.length) {
        for (const provisional of match.companionProvisionals) {
          try {
            await mainClientService.dismissEmail(
              tradingEntityId,
              provisional.emailId,
              provisional.tradeIndex,
              provisional.bankTradeNumber,
            );
          } catch (dismissErr) {
            // Non-fatal: provisional stays in the tray but doesn't cause harm
            console.warn('KAN-260: Failed to dismiss companion provisional:', provisional.bankTradeNumber, dismissErr);
          }
        }
      }
    } catch {
      showNotification(t('tradeBooking.messages.confirmationReprocessFailed'), 'error');
    } finally {
      setIsReprocessing(false);
      setPendingConfirmationMatch(null);
    }
  };

  // Trade Image Capture handler (KAN-82)
  // Builds a single update object and applies it atomically to avoid
  // cascading handler side-effects (e.g. handleTradeTypeChange resetting dates).
  const handleExtractionComplete = (result: TradeImageCaptureResult) => {
   try {
    const confidence: Record<string, number> = {};
    const rawText: Record<string, string> = {};
    const updates: Partial<typeof form> = {};
    const fields = result.extractedFields;

    if (!fields || typeof fields !== 'object') {
      showNotification(t('tradeBooking.imageCapture.extractionError'), 'error');
      setCaptureResetTrigger(prev => prev + 1);
      return;
    }

    const getVal = (key: string) => fields[key]?.value;
    const hasField = (key: string) => fields[key] !== undefined && fields[key].value !== null;

    // Reject extraction if insufficient core trade fields were found.
    // Require at least amount OR price (a real trade always has a number) PLUS
    // at least one other core field. This prevents non-trade images (e.g.
    // PowerPoint ribbon containing "USD") from being accepted.
    const hasAmountOrPrice = hasField('amount') || hasField('price');
    const otherCoreFields = ['tradeType', 'direction', 'currency1', 'currency2'];
    const otherCoreCount = otherCoreFields.filter(f => hasField(f)).length;
    if (!hasAmountOrPrice || otherCoreCount === 0) {
      showNotification(t('tradeBooking.imageCapture.noDataFound'), 'warning');
      setCaptureResetTrigger(prev => prev + 1); // reset panel from "completed" back to "ready"
      return;
    }
    // Parse YYYY-MM-DD as local date (not UTC) to avoid timezone off-by-one
    const parseLocalDate = (val: string): Date | null => {
      if (!val || typeof val !== 'string') return null;
      const parts = val.split('-').map(Number);
      if (parts.length !== 3 || parts.some(isNaN)) return null;
      const [y, m, d] = parts;
      const date = new Date(y, m - 1, d);
      return isNaN(date.getTime()) ? null : date;
    };
    // Parse numeric value safely, returning null for NaN
    const parseNum = (val: any): number | null => {
      const n = typeof val === 'number' ? val : parseFloat(val);
      return isNaN(n) ? null : n;
    };

    // Record confidence and rawText for all extracted fields
    for (const [fieldName, fieldData] of Object.entries(fields)) {
      if (fieldData.value !== null && fieldData.value !== undefined) {
        confidence[fieldName] = fieldData.confidence;
        if (fieldData.rawText) rawText[fieldName] = fieldData.rawText;
      }
    }

    // --- Trade type ---
    if (hasField('tradeType')) {
      const extractedType = getVal('tradeType');
      if (extractedType === 'FORWARD_ASIATICO') {
        updates.tradeType = TradeType.FORWARD;
        updates.isAverageRateForward = true;
      } else {
        updates.tradeType = extractedType === 'FORWARD' ? TradeType.FORWARD : TradeType.SPOT;
      }
    }

    // --- Direction ---
    if (hasField('direction')) {
      updates.direction = getVal('direction') === 'BUY' ? Direction.BUY : Direction.SELL;
    }

    // --- Currencies ---
    if (hasField('currency1')) updates.currency1 = String(getVal('currency1')).toUpperCase();
    if (hasField('currency2')) updates.currency2 = String(getVal('currency2')).toUpperCase();

    // --- Amount & Price ---
    if (hasField('amount')) {
      const amt = parseNum(getVal('amount'));
      if (amt !== null) {
        updates.amount = amt;
        setAmountInput(formatAmountDisplay(amt, locale));
      }
    }
    if (hasField('price')) {
      const px = parseNum(getVal('price'));
      if (px !== null) {
        updates.price = px;
        setPriceInput(formatPriceDisplay(px, locale));
      }
    }
    if (hasField('spotPrice')) {
      const sp = parseNum(getVal('spotPrice'));
      if (sp !== null) {
        updates.spotPrice = sp;
        setSpotPriceInput(formatPriceDisplay(sp, locale));
      }
    }
    if (hasField('forwardPoints')) {
      const fp = parseNum(getVal('forwardPoints'));
      if (fp !== null) {
        updates.forwardPoints = fp;
        setForwardPointsInput(formatPriceDisplay(fp, locale));
      }
    }

    // --- Dates (set all at once to avoid cascading recalculations) ---
    const parsedTradeDate = hasField('tradeDate') ? parseLocalDate(getVal('tradeDate')) : null;
    const parsedValueDate = hasField('valueDate') ? parseLocalDate(getVal('valueDate')) : null;
    const expiryVal = getVal('expiryDate') || getVal('maturityDate');
    const parsedExpiryDate = expiryVal ? parseLocalDate(expiryVal) : null;
    const parsedPaymentDate = hasField('paymentDate') ? parseLocalDate(getVal('paymentDate')) : null;

    if (parsedTradeDate) updates.tradeDate = parsedTradeDate;
    if (parsedValueDate) updates.valueDate = parsedValueDate;
    if (parsedExpiryDate) {
      updates.expiryDate = parsedExpiryDate;
      confidence['expiryDate'] = confidence['expiryDate'] || confidence['maturityDate'];
    }
    if (parsedPaymentDate) updates.paymentDate = parsedPaymentDate;

    // --- Maturity (days + formatted string) ---
    if (hasField('maturityDays')) {
      const days = parseNum(getVal('maturityDays'));
      if (days !== null) {
        updates.maturityDays = days;
        updates.maturity = formatMaturity(days);
      }
    } else if (updates.valueDate && updates.expiryDate) {
      // Derive maturityDays from the two dates
      const days = calculateDaysDifference(updates.valueDate, updates.expiryDate);
      updates.maturityDays = days;
      updates.maturity = formatMaturity(days);
    }

    // --- Payment date: derive if not extracted ---
    if (!updates.paymentDate && updates.expiryDate) {
      updates.paymentDate = addDays(updates.expiryDate, updates.tradeType === TradeType.SPOT ? 0 : 1);
    }

    // --- Settlement ---
    if (hasField('settlementType')) {
      const stVal = getVal('settlementType');
      if (stVal === 'COMPENSACION') updates.settlementType = SettlementType.COMPENSACION;
      else if (stVal === 'ENTREGA_FISICA') updates.settlementType = SettlementType.ENTREGA_FISICA;
    }
    // Valuta dates and fixing fields depend on settlement type
    const effectiveSettlement = updates.settlementType || form.settlementType;

    if (effectiveSettlement === SettlementType.ENTREGA_FISICA) {
      // Entrega Física: clear fixing fields and settlement currency regardless of what was extracted
      updates.fixingReference = FixingReference.NA;
      updates.fixingOffset = FixingOffset.NA;
      updates.settlementCurrency = '';
      // Set valuta dates
      if (updates.expiryDate) {
        updates.currency1PaymentDate = hasField('currency1PaymentDate')
          ? (parseLocalDate(getVal('currency1PaymentDate')) || updates.expiryDate)
          : updates.expiryDate;
        updates.currency2PaymentDate = hasField('currency2PaymentDate')
          ? (parseLocalDate(getVal('currency2PaymentDate')) || updates.expiryDate)
          : updates.expiryDate;
      }
    } else {
      // Compensación: apply extracted fixing fields
      if (hasField('settlementCurrency')) updates.settlementCurrency = String(getVal('settlementCurrency'));
      if (hasField('fixingReference')) {
        const frVal = getVal('fixingReference');
        if (['USD_OBS', 'UF', 'BLOOMBERG_11AM', 'N/A'].includes(frVal)) {
          updates.fixingReference = frVal as FixingReference;
        }
      }
      if (hasField('fixingOffset')) {
        const foVal = getVal('fixingOffset');
        if (['0', '-1', '-2', 'N/A'].includes(foVal)) {
          updates.fixingOffset = foVal as FixingOffset;
        }
      }
      // Clear valuta dates for Compensación
      updates.currency1PaymentDate = null;
      updates.currency2PaymentDate = null;
    }

    // --- Parties (only apply if entity lookup matches a dropdown option) ---
    if (hasField('ourParty')) {
      const entity = tradingEntities.find(
        e => e.name.toLowerCase() === String(getVal('ourParty')).toLowerCase()
      );
      if (entity) {
        updates.ourParty = entity.name;
        updates.ourPartyId = entity.id;
        updates.ourPartyTaxId = entity.taxId || '';
      } else {
        // Extracted value doesn't match any trading entity — treat as not found
        delete confidence['ourParty'];
      }
    }
    if (hasField('counterparty')) {
      const bank = banks.find(
        b => b.name.toLowerCase() === String(getVal('counterparty')).toLowerCase()
      );
      if (bank) {
        updates.counterparty = bank.name;
        updates.counterpartyId = bank.id;
        updates.counterpartyTaxId = bank.taxId || '';
      } else {
        // Extracted value doesn't match any bank — treat as not found
        delete confidence['counterparty'];
      }
    }

    // --- Average rate forward ---
    if (hasField('isAverageRateForward')) {
      updates.isAverageRateForward = Boolean(getVal('isAverageRateForward'));
    }

    // --- Groupings ---
    if (hasField('grouping1')) updates.grouping1 = String(getVal('grouping1'));
    if (hasField('grouping2')) updates.grouping2 = String(getVal('grouping2'));
    if (hasField('grouping3')) updates.grouping3 = String(getVal('grouping3'));

    // --- Classification & Notes ---
    if (hasField('hedgeObject')) updates.hedgeObject = String(getVal('hedgeObject'));
    if (hasField('notes')) updates.notes = String(getVal('notes'));

    // Prevent convention useEffects from overriding extracted values
    setDatesManuallyModified(true);

    // Apply all at once
    bulkUpdateFields(updates);
    setCaptureConfidenceValues(confidence, rawText);
    showNotification(
      `${result.sourceTypeName}: ${result.fieldsPopulated} fields populated`,
      'success'
    );
   } catch (err) {
    console.error('Failed to apply extracted trade data:', err);
    showNotification(t('tradeBooking.imageCapture.extractionError'), 'error');
   }
  };

  // Multi-Trade handler (KAN-125)
  const handleMultiTradeDetected = useCallback(async (
    imageBase64: string, imageMimeType: string, imageFileSizeBytes: number, sourceTypeName: string
  ) => {
    const currentClientId = user?.organization?.id || user?.id || '';
    try {
      setIsExtracting(true);
      const result = await tradeImageCaptureService.extractMultiTrade(
        currentClientId, imageBase64, imageMimeType, imageFileSizeBytes
      );
      console.log('[MultiTrade] Extraction result:', JSON.stringify(result, null, 2));

      // Clear single-trade form if in new trade mode
      if (!isViewingTrade) {
        resetForm();
        clearAllConfidence();
      }

      setMultiTradeResult(result);
      setMultiTradeModalOpen(true);
    } catch (err) {
      console.error('Multi-trade extraction failed:', err);
      showNotification(t('tradeBooking.imageCapture.extractionError'), 'error');
      setCaptureResetTrigger(prev => prev + 1);
    } finally {
      setIsExtracting(false);
    }
  }, [user, isViewingTrade, resetForm, clearAllConfidence, showNotification, t]);

  const handleMultiTradeModalClose = useCallback(() => {
    setMultiTradeModalOpen(false);
    setMultiTradeResult(null);
    setCaptureResetTrigger(prev => prev + 1);
    setGridRefreshTrigger(prev => prev + 1);
    setSelectedDraftId(null);
  }, []);

  // Draft picker handlers — right-click on Layers button (KAN-187e)
  const handleLayersContextMenu = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    const cid = user?.organization?.id || user?.id;
    if (!cid) return;
    setDraftPickerPos({ x: e.clientX, y: e.clientY });
    setDraftPickerOpen(true);
    setDraftPickerLoading(true);
    try {
      const drafts = await listMultiTradeDrafts(cid);
      setDraftPickerDrafts(drafts);
    } catch {
      setDraftPickerDrafts([]);
    } finally {
      setDraftPickerLoading(false);
    }
  }, [user]);

  const handleDraftPickerSelect = useCallback((draftId: string) => {
    setDraftPickerOpen(false);
    setSelectedDraftId(draftId);
    setMultiTradeModalOpen(true);
  }, []);

  const handleDraftPickerClose = useCallback(() => {
    setDraftPickerOpen(false);
  }, []);

  // Quote handlers
  const handleOpenQuoteModal = () => {
    setShowQuoteModal(true);
  };

  const handleQuoteSelected = (quote: Quote) => {
    setActiveQuote(quote);
    setShowQuoteModal(false);

    // Auto-fill price field
    const formattedPrice = formatPriceDisplay(quote.quotedPrice, locale);
    setPriceInput(formattedPrice);
    updateField('price', quote.quotedPrice);

    // Auto-select counterparty (find the bank by ID)
    const bank = banks.find(b => b.id === quote.counterparty);
    if (bank) {
      updateField('counterparty', bank.name);
      updateField('counterpartyId', bank.id);
      updateField('counterpartyTaxId', bank.taxId);
    }

    showNotification(t('tradeBooking.quote.received', 'Quote received successfully'), 'success');
  };

  const handleQuoteExpired = () => {
    setActiveQuote(null);

    // Clear price field
    setPriceInput('');
    updateField('price', null);

    // Clear counterparty if it was auto-selected from quote
    if (activeQuote) {
      updateField('counterparty', '');
      updateField('counterpartyId', '');
      updateField('counterpartyTaxId', '');
    }

    showNotification(t('tradeBooking.quote.expired', 'Quote has expired'), 'warning');
  };

  // Helper function to find matching date convention
  const findDateConvention = (tradeType: TradeType | null, currencyPair: string): DateConvention | null => {
    if (!tradeType) return null;

    // First, try to find exact match for currency pair
    const exactMatch = dateConventions.find(
      conv => conv.tradeType === tradeType && conv.currencyPair === currencyPair
    );
    if (exactMatch) return exactMatch;

    // Fall back to "All Others" for this trade type
    const fallback = dateConventions.find(
      conv => conv.tradeType === tradeType && conv.currencyPair === 'All Others'
    );
    return fallback || null;
  };

  // Helper function to calculate actual date from convention key
  const calculateDateFromConvention = (
    conventionKey: string,
    tradeDate: Date,
    valueDate: Date | null,
    expiryDate: Date | null,
    useBusinessDays: boolean
  ): Date | null => {
    if (!conventionKey) return null;

    // Handle "N/A" case
    if (conventionKey === 'notApplicable') return null;

    // Always use business days (skip weekends + holidays)
    const addDays = (baseDate: Date, days: number): Date => {
      return businessCalendar.addBusinessDays(baseDate, days);
    };

    // Map keys to date calculations
    switch (conventionKey) {
      // Today variants
      case 'today':
        return businessCalendar.getNextBusinessDay(tradeDate);
      case 'todayPlus1':
        return addDays(tradeDate, 1);
      case 'todayPlus2':
        return addDays(tradeDate, 2);
      case 'todayMinus1':
        return addDays(tradeDate, -1);

      // Trade Date variants
      case 'tradeDateOption':
        return tradeDate;
      case 'tradeDatePlus1':
        return addDays(tradeDate, 1);
      case 'tradeDatePlus2':
        return addDays(tradeDate, 2);

      // Value Date variants
      case 'valueDateOption':
        return valueDate;
      case 'valueDatePlus1':
        return valueDate ? addDays(valueDate, 1) : null;
      case 'valueDatePlus2':
        return valueDate ? addDays(valueDate, 2) : null;

      // Expiry Date variants
      case 'expiryDateOption':
        return expiryDate;
      case 'expiryDatePlus1':
        return expiryDate ? addDays(expiryDate, 1) : null;
      case 'expiryDatePlus2':
        return expiryDate ? addDays(expiryDate, 2) : null;

      default:
        return null;
    }
  };

  // Auto-populate date fields based on trade type and currency pair
  const applyDateConvention = (tradeType: TradeType | null, currency1: string | null, currency2: string | null) => {
    if (!tradeType || !currency1 || !currency2) return;

    const currencyPair = `${currency1}/${currency2}`;
    const convention = findDateConvention(tradeType, currencyPair);

    if (!convention) return;

    // Use override values if enabled, otherwise use defaults
    const tradeDateConvention = convention.hasOverride && convention.overrideTradeDate
      ? convention.overrideTradeDate
      : convention.defaultTradeDate;

    const valueDateConvention = convention.hasOverride && convention.overrideValueDate
      ? convention.overrideValueDate
      : convention.defaultValueDate;

    const expiryDateConvention = convention.hasOverride && convention.overrideExpiryDate
      ? convention.overrideExpiryDate
      : convention.defaultExpiryDate;

    const paymentDateConvention = convention.hasOverride && convention.overridePaymentDate
      ? convention.overridePaymentDate
      : convention.defaultPaymentDate;

    // Calculate actual dates
    // Start with trade date (base reference point) — use systemDate if available
    const baseTradeDate = systemDate ? new Date(systemDate + 'T00:00:00') : new Date();
    const useBusinessDays = convention.useBusinessDays;
    const calculatedTradeDate = calculateDateFromConvention(tradeDateConvention, baseTradeDate, null, null, useBusinessDays);

    if (calculatedTradeDate) {
      updateField('tradeDate', calculatedTradeDate);

      // Calculate value date (depends on trade date)
      // Skip for Forward Starting — ValueDate is driven by the fixing schedule, not conventions
      const calculatedValueDate = calculateDateFromConvention(valueDateConvention, calculatedTradeDate, null, null, useBusinessDays);
      if (calculatedValueDate && !form.isForwardStarting) {
        updateField('valueDate', calculatedValueDate);

        // Calculate expiry date (depends on value date)
        const calculatedExpiryDate = calculateDateFromConvention(expiryDateConvention, calculatedTradeDate, calculatedValueDate, null, useBusinessDays);
        if (calculatedExpiryDate) {
          updateField('expiryDate', calculatedExpiryDate);

          // Calculate payment date (depends on expiry date)
          const calculatedPaymentDate = calculateDateFromConvention(paymentDateConvention, calculatedTradeDate, calculatedValueDate, calculatedExpiryDate, useBusinessDays);
          if (calculatedPaymentDate) {
            updateField('paymentDate', calculatedPaymentDate);
          }
        } else if (expiryDateConvention === 'notApplicable') {
          // For Forward trades, expiry date is N/A - clear it
          updateField('expiryDate', null);
        }
      }
    }
  };

  // Load section data in parallel on mount — each section tracks its own loading state
  useEffect(() => {
    const clientId = user?.organization?.id || user?.id || '';

    // Shortcuts
    (async () => {
      try {
        const loadedShortcuts = await getTradeBookingShortcuts();
        if (loadedShortcuts.length > 0) {
          setShortcuts(loadedShortcuts);
        }
      } catch (error) {
        console.error('Failed to load shortcuts:', error);
      } finally {
        setLoadingShortcuts(false);
      }
    })();

    // Core trade data: trading entities + banks (needed for dropdowns)
    (async () => {
      try {
        const [entitiesResponse, banksResponse] = await Promise.all([
          clientService.getTradingEntities(clientId),
          bankService.listBanks(),
        ]);
        if (entitiesResponse.success) setTradingEntities(entitiesResponse.data);
        if (banksResponse.success) setBanks(banksResponse.data.filter(b => b.isCounterparty !== false));
      } catch (error) {
        console.error('Failed to load core trade data:', error);
      } finally {
        setLoadingCoreData(false);
      }
    })();

    // Business calendar: load holidays from Firestore (default SCL for client)
    businessCalendar.loadFromAPI(['SCL']).then(() => setCalendarVersion(v => v + 1));

    // Classification: groupings config
    (async () => {
      try {
        const config = await groupingsService.getGroupings(clientId);
        if (config) setGroupingsConfig(config);
      } catch (error) {
        console.error('Failed to load groupings:', error);
      } finally {
        setLoadingClassification(false);
      }
    })();

    // Date & Fixing conventions (both depend on user preferences)
    (async () => {
      try {
        const [userPreferences, marketDateConventions, marketFixingConventions] = await Promise.all([
          getUserPreferences(),
          getDateConventions(),
          getFixingExpiryConventions(),
        ]);

        // Merge date conventions with user overrides
        const userDateOverrides = userPreferences.tradeBooking?.dateConventions || {};
        const mergedDateConventions = marketDateConventions.map((convention) => {
          const override = userDateOverrides[convention.id];
          return {
            id: convention.id,
            tradeType: convention.tradeType as TradeType,
            currencyPair: convention.currencyPair,
            defaultTradeDate: convention.defaultTradeDate,
            defaultValueDate: convention.defaultValueDate,
            defaultExpiryDate: convention.defaultExpiryDate,
            defaultPaymentDate: convention.defaultPaymentDate,
            useBusinessDays: convention.useBusinessDays,
            hasOverride: override?.hasOverride || false,
            overrideTradeDate: override?.overrideTradeDate,
            overrideValueDate: override?.overrideValueDate,
            overrideExpiryDate: override?.overrideExpiryDate,
            overridePaymentDate: override?.overridePaymentDate,
          };
        });
        setDateConventions(mergedDateConventions);
        setLoadingDates(false);

        // Merge fixing/expiry conventions with user overrides
        const userFixingOverrides = userPreferences.tradeBooking?.fixingExpiryConventions || {};
        const mergedFixingConventions = marketFixingConventions.map((convention) => {
          const override = userFixingOverrides[convention.id];
          return {
            id: convention.id,
            tradeType: TradeType.FORWARD,
            currencyPair: convention.currencyPair,
            defaultFixingReference: convention.defaultFixingReference as FixingReference,
            defaultFixingOffset: convention.defaultFixingOffset as FixingOffset,
            defaultSettlementType: convention.defaultSettlementType as SettlementType,
            defaultSettlementCurrency: convention.defaultSettlementCurrency,
            hasOverride: override?.hasOverride || false,
            overrideFixingReference: override?.overrideFixingReference as FixingReference | undefined,
            overrideFixingOffset: override?.overrideFixingOffset as FixingOffset | undefined,
            overrideSettlementType: override?.overrideSettlementType as SettlementType | undefined,
            overrideSettlementCurrency: override?.overrideSettlementCurrency,
          };
        });
        setFixingExpiryConventions(mergedFixingConventions);
      } catch (error) {
        console.error('Failed to load conventions:', error);
      } finally {
        setLoadingDates(false);
        setLoadingFixing(false);
      }
    })();
  }, []);

  // Load FXGO pending corrections count
  const loadFxgoCorrectionsCount = useCallback(async () => {
    const cId = user?.organization?.id || user?.id || '';
    if (!cId) return;
    try {
      const result = await mainClientService.getFxgoPendingCorrections(cId);
      if (result.success && result.data) {
        setFxgoCorrectionsCount(result.data.totalCount || 0);
      }
    } catch {
      // silently ignore
    }
  }, [user]);

  useEffect(() => {
    loadFxgoCorrectionsCount();
  }, [loadFxgoCorrectionsCount]);

  // SSE: listen for FXGO events to refresh grid + badge in real time
  useEventStreamSimple({
    clientId: user?.organization?.id || user?.id || '',
    eventTypes: ['fxgo_trade_created', 'fxgo_correction_received'],
    onEvent: (eventData) => {
      if (eventData.type === 'fxgo_trade_created' || eventData.type === 'fxgo_correction_received') {
        setGridRefreshTrigger(prev => prev + 1);
      }
      if (eventData.type === 'fxgo_correction_received') {
        loadFxgoCorrectionsCount();
      }
    },
  });

  // Watch for changes in Trade Type and Currency Pair
  // Only auto-populate if dates haven't been manually modified
  useEffect(() => {
    if (!datesManuallyModified) {
      applyDateConvention(form.tradeType, form.currency1, form.currency2);
    }
  }, [form.tradeType, form.currency1, form.currency2]);

  // When user changes date conventions in settings, re-apply if dates haven't been manually modified
  useEffect(() => {
    if (!datesManuallyModified) {
      applyDateConvention(form.tradeType, form.currency1, form.currency2);
    }
  }, [dateConventions]);

  // Helper function to find matching fixing/expiry convention
  const findFixingExpiryConvention = (tradeType: TradeType | null, currencyPair: string): FixingExpiryConvention | null => {
    if (!tradeType || tradeType !== TradeType.FORWARD) return null; // Only for Forward trades

    // First, try to find exact match for currency pair
    const exactMatch = fixingExpiryConventions.find(
      conv => conv.tradeType === tradeType && conv.currencyPair === currencyPair
    );
    if (exactMatch) return exactMatch;

    // Fall back to "All Others" for this trade type
    const fallback = fixingExpiryConventions.find(
      conv => conv.tradeType === tradeType && conv.currencyPair === 'All Others'
    );
    return fallback || null;
  };

  // Auto-populate fixing & expiry fields based on trade type and currency pair
  const applyFixingExpiryConvention = (tradeType: TradeType | null, currency1: string | null, currency2: string | null) => {
    if (!tradeType || tradeType !== TradeType.FORWARD || !currency1 || !currency2) {
      return;
    }

    // CLF/CLP (Forward de Inflación) has fixed business rules — convention application would override them
    if (currency1 === 'CLF') return;

    const currencyPair = `${currency1}/${currency2}`;
    const convention = findFixingExpiryConvention(tradeType, currencyPair);

    if (!convention) return;

    // Use override values if enabled, otherwise use defaults
    const fixingReference = convention.hasOverride && convention.overrideFixingReference
      ? convention.overrideFixingReference
      : convention.defaultFixingReference;

    const fixingOffset = convention.hasOverride && convention.overrideFixingOffset
      ? convention.overrideFixingOffset
      : convention.defaultFixingOffset;

    const settlementType = convention.hasOverride && convention.overrideSettlementType
      ? convention.overrideSettlementType
      : convention.defaultSettlementType;

    const settlementCurrency = convention.hasOverride && convention.overrideSettlementCurrency !== undefined
      ? convention.overrideSettlementCurrency
      : convention.defaultSettlementCurrency;

    // Apply the convention values to the form
    updateField('fixingReference', fixingReference);
    updateField('fixingOffset', fixingOffset);
    handleSettlementTypeChange(settlementType);
    updateField('settlementCurrency', settlementCurrency);
  };

  // Watch for changes in Trade Type and Currency Pair for Fixing & Expiry
  // Skip when applying extracted values (datesManuallyModified is set before bulk update)
  useEffect(() => {
    if (form.tradeType === TradeType.FORWARD && !datesManuallyModified) {
      applyFixingExpiryConvention(form.tradeType, form.currency1, form.currency2);
    }
  }, [form.tradeType, form.currency1, form.currency2]);

  // When user changes fixing/expiry conventions in settings, re-apply
  useEffect(() => {
    if (form.tradeType === TradeType.FORWARD && !datesManuallyModified) {
      applyFixingExpiryConvention(form.tradeType, form.currency1, form.currency2);
    }
  }, [fixingExpiryConventions]);

  const handleAmountBlur = () => {
    const parsed = parseAmountInput(amountInput);
    updateField('amount', parsed);
    if (parsed !== null) {
      setAmountInput(formatAmountDisplay(parsed, locale, 2));
    }
  };

  const handlePriceBlur = () => {
    const parsed = parseAmountInput(priceInput);
    updateField('price', parsed);
    if (parsed !== null) {
      setPriceInput(formatPriceDisplay(parsed, locale, 4));
      // Skip triangular calculation for Forward Starting (price is placeholder 0)
      if (form.tradeType === TradeType.FORWARD && !form.isForwardStarting) {
        if (form.spotPrice !== null) {
          // Back-calculate forward points: ForwardPoints = Price - SpotPrice
          const calculatedPoints = parsed - form.spotPrice;
          updateField('forwardPoints', calculatedPoints);
          setForwardPointsInput(formatPriceDisplay(calculatedPoints, locale, 4));
        } else if (form.forwardPoints !== null) {
          // Back-calculate spot price: SpotPrice = Price - ForwardPoints
          const calculatedSpot = parsed - form.forwardPoints;
          updateField('spotPrice', calculatedSpot);
          setSpotPriceInput(formatPriceDisplay(calculatedSpot, locale, 4));
        }
      }
    }
  };

  const handleSpotPriceBlur = () => {
    const parsed = parseAmountInput(spotPriceInput);
    updateField('spotPrice', parsed);
    if (parsed !== null) {
      setSpotPriceInput(formatPriceDisplay(parsed, locale, 4));
      // Skip triangular calculation for Forward Starting (spot is unknown at booking)
      if (!form.isForwardStarting) {
        if (form.forwardPoints !== null) {
          // Primary: Price = SpotPrice + ForwardPoints
          const calculatedPrice = parsed + form.forwardPoints;
          updateField('price', calculatedPrice);
          setPriceInput(formatPriceDisplay(calculatedPrice, locale, 4));
        } else if (form.price !== null) {
          // Back-calculate: ForwardPoints = Price - SpotPrice
          const calculatedPoints = form.price - parsed;
          updateField('forwardPoints', calculatedPoints);
          setForwardPointsInput(formatPriceDisplay(calculatedPoints, locale, 4));
        }
      }
    }
  };

  const handleForwardPointsBlur = () => {
    const parsed = parseAmountInput(forwardPointsInput);
    updateField('forwardPoints', parsed);
    if (parsed !== null) {
      setForwardPointsInput(formatPriceDisplay(parsed, locale, 4));
      // Skip triangular calculation for Forward Starting (spot/price unknown at booking)
      if (!form.isForwardStarting) {
        if (form.spotPrice !== null) {
          // Primary: Price = SpotPrice + ForwardPoints
          const calculatedPrice = form.spotPrice + parsed;
          updateField('price', calculatedPrice);
          setPriceInput(formatPriceDisplay(calculatedPrice, locale, 4));
        } else if (form.price !== null) {
          // Back-calculate: SpotPrice = Price - ForwardPoints
          const calculatedSpot = form.price - parsed;
          updateField('spotPrice', calculatedSpot);
          setSpotPriceInput(formatPriceDisplay(calculatedSpot, locale, 4));
        }
      }
    }
  };

  const handleSaveDraft = () => {
    // Only check free-text field length limits — full validation is not required for drafts
    if (notesError) {
      showNotification(notesError, 'error');
      return;
    }
    if (hedgeObjectError) {
      showNotification(hedgeObjectError, 'error');
      return;
    }
    // Skip confirmation dialog for drafts — directly execute the save
    handleConfirm('draft');
  };

  const handleSubmit = () => {
    // Validate allocation totals if allocation rows exist
    if (allocationRows.length > 0) {
      const allocTotal = allocationRows.reduce((sum, row) => sum + (row.amount || 0), 0);
      if (!form.amount || Math.abs(allocTotal - form.amount) > 0.01) {
        showNotification(t('tradeBooking.allocation.totalMismatch', {
          total: allocTotal.toLocaleString(),
          expected: (form.amount || 0).toLocaleString()
        }), 'error');
        return;
      }
    }

    // Check text field validation first
    if (notesError) {
      showNotification(notesError, 'error');
      return;
    }
    if (hedgeObjectError) {
      showNotification(hedgeObjectError, 'error');
      return;
    }

    const { isValid, errors: validationErrors } = validate();

    if (!isValid) {
      // Get error count to determine message type
      const errorCount = Object.keys(validationErrors).length;

      if (errorCount === 1) {
        // Single error - show specific error message
        const errorKey = Object.keys(validationErrors)[0] as keyof typeof validationErrors;
        const errorMessage = validationErrors[errorKey];
        if (errorMessage) {
          showNotification(t(errorMessage), 'error');
        }
      } else if (errorCount > 1) {
        // Multiple errors - show generic message
        showNotification(t('tradeBooking.validation.multipleErrors'), 'error');
      }
      return;
    }
    setConfirmationType(canDirectlyBook ? 'submit' : 'approval');
    setShowConfirmation(true);
  };

  const handleUpdate = () => {
    // Validate before updating existing trade
    if (notesError) {
      showNotification(notesError, 'error');
      return;
    }
    if (hedgeObjectError) {
      showNotification(hedgeObjectError, 'error');
      return;
    }

    // Draft updates only need minimal validation (same as Save Draft)
    // Full validation is only required for Booked trade updates
    if (editingWorkflowState !== 'Draft') {
      const { isValid, errors: validationErrors } = validate();

      if (!isValid) {
        const errorCount = Object.keys(validationErrors).length;
        if (errorCount === 1) {
          const errorKey = Object.keys(validationErrors)[0] as keyof typeof validationErrors;
          const errorMessage = validationErrors[errorKey];
          if (errorMessage) {
            showNotification(t(errorMessage), 'error');
          }
        } else if (errorCount > 1) {
          showNotification(t('tradeBooking.validation.multipleErrors'), 'error');
        }
        return;
      }
    }
    setConfirmationType('update');
    setShowConfirmation(true);
  };

  const handleBookDraft = () => {
    // Validate allocation totals if allocation rows exist
    if (allocationRows.length > 0) {
      const allocTotal = allocationRows.reduce((sum, row) => sum + (row.amount || 0), 0);
      if (!form.amount || Math.abs(allocTotal - form.amount) > 0.01) {
        showNotification(t('tradeBooking.allocation.totalMismatch', {
          total: allocTotal.toLocaleString(),
          expected: (form.amount || 0).toLocaleString()
        }), 'error');
        return;
      }
    }

    if (notesError) {
      showNotification(notesError, 'error');
      return;
    }
    if (hedgeObjectError) {
      showNotification(hedgeObjectError, 'error');
      return;
    }

    const { isValid, errors: validationErrors } = validate();

    if (!isValid) {
      const errorCount = Object.keys(validationErrors).length;
      if (errorCount === 1) {
        const errorKey = Object.keys(validationErrors)[0] as keyof typeof validationErrors;
        const errorMessage = validationErrors[errorKey];
        if (errorMessage) {
          showNotification(t(errorMessage), 'error');
        }
      } else if (errorCount > 1) {
        showNotification(t('tradeBooking.validation.multipleErrors'), 'error');
      }
      return;
    }
    setConfirmationType('book_draft');
    setShowConfirmation(true);
  };

  const handleRequestQuoteApproval = async () => {
    if (!editingTradeId || !form.ourPartyId) return;
    setIsLoading(true);
    try {
      const result = await quoteApprovalService.requestApproval(form.ourPartyId, editingTradeId);
      if (result.success) {
        showNotification(t('tradeBooking.quoteApproval.requestSubmitted', 'Approval request submitted'), 'success');
        // Update local state with the returned quoteApproval
        const updatedQA = result.data?.quoteApproval;
        if (updatedQA) setEditingQuoteApproval(updatedQA);
        setGridRefreshTrigger(prev => prev + 1);
      }
    } catch (error: any) {
      showNotification(error.message || 'Failed to request quote approval', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    resetForm();
    setAmountInput('');
    setPriceInput('');
    setSpotPriceInput('');
    setForwardPointsInput('');
    setDatesManuallyModified(false); // Reset flag to allow auto-population again
    setMissedFields([]); // Clear yellow highlights
    setActiveQuote(null); // Clear any active quote
    setNotesError(null); // Clear notes validation error
    setAllocationRows([]); // Clear allocation
    setCaptureResetTrigger(prev => prev + 1); // Reset capture panel
    setIsViewingTrade(false); // Exit view mode
    setEditingTradeId(null); // Clear editing state
    setEditingTradeClientId(null);
    setEditingWorkflowState(null);
    setEditingQuoteApproval(null);
    setGridClearSelectionTrigger(prev => prev + 1); // Deselect grid row
  };

  // Shortcut handlers
  const handleShortcutClick = (shortcut: Shortcut) => {
    // Auto-fill form with shortcut data
    if (shortcut.tradeType) handleTradeTypeChange(shortcut.tradeType);
    if (shortcut.direction) updateField('direction', shortcut.direction);
    if (shortcut.currency1) updateField('currency1', shortcut.currency1);
    if (shortcut.currency2) updateField('currency2', shortcut.currency2);
    if (shortcut.amount !== undefined) {
      updateField('amount', shortcut.amount);
      if (shortcut.amount) setAmountInput(formatAmountDisplay(shortcut.amount, locale, 2));
    }
    if (shortcut.ourParty) {
      const selectedEntity = tradingEntities.find(e => e.name === shortcut.ourParty);
      updateField('ourParty', shortcut.ourParty);
      updateField('ourPartyId', selectedEntity?.id || '');
      updateField('ourPartyTaxId', selectedEntity?.taxId || '');
    }
    if (shortcut.counterparty) {
      const selectedBank = banks.find(b => b.name === shortcut.counterparty);
      updateField('counterparty', shortcut.counterparty);
      updateField('counterpartyId', selectedBank?.id || '');
      updateField('counterpartyTaxId', selectedBank?.taxId || '');
    }
  };

  const handleNewShortcut = () => {
    if (shortcuts.length >= 10) {
      showNotification(t('tradeBooking.shortcuts.maxReached'), 'error');
      return;
    }
    setEditingShortcut({
      id: Date.now().toString(),
      name: '',
    });
    setIsNewShortcut(true);
  };

  const handleEditShortcut = (shortcut: Shortcut) => {
    setEditingShortcut({ ...shortcut });
    setIsNewShortcut(false);
  };

  const handleDuplicateShortcut = (shortcut: Shortcut) => {
    if (shortcuts.length >= 10) {
      showNotification(t('tradeBooking.shortcuts.maxReached'), 'error');
      return;
    }
    setEditingShortcut({
      ...shortcut,
      id: Date.now().toString(),
      name: `${shortcut.name} (Copy)`,
    });
    setIsNewShortcut(true);
  };

  const handleDeleteShortcut = async (id: string) => {
    const updatedShortcuts = shortcuts.filter(s => s.id !== id);
    setShortcuts(updatedShortcuts);

    try {
      await saveTradeBookingShortcuts(updatedShortcuts);
      showNotification(t('tradeBooking.shortcuts.deleted'), 'success');
    } catch (error) {
      console.error('Failed to save shortcuts:', error);
      showNotification(t('tradeBooking.shortcuts.saveFailed'), 'error');
    }
  };

  const handleSaveShortcut = async () => {
    if (!editingShortcut?.name.trim()) {
      showNotification(t('tradeBooking.shortcuts.nameRequired'), 'error');
      return;
    }

    let updatedShortcuts: Shortcut[];
    if (isNewShortcut) {
      updatedShortcuts = [...shortcuts, editingShortcut];
    } else {
      updatedShortcuts = shortcuts.map(s => s.id === editingShortcut.id ? editingShortcut : s);
    }

    setShortcuts(updatedShortcuts);

    try {
      await saveTradeBookingShortcuts(updatedShortcuts);
      showNotification(
        isNewShortcut ? t('tradeBooking.shortcuts.created') : t('tradeBooking.shortcuts.updated'),
        'success'
      );
    } catch (error) {
      console.error('Failed to save shortcuts:', error);
      showNotification(t('tradeBooking.shortcuts.saveFailed'), 'error');
    }

    setEditingShortcut(null);
    setIsNewShortcut(false);
  };

  const handleCancelShortcutEdit = () => {
    setEditingShortcut(null);
    setIsNewShortcut(false);
  };

  const handleUpdateEditingShortcut = (field: string, value: any) => {
    if (!editingShortcut) return;
    setEditingShortcut({ ...editingShortcut, [field]: value });
  };

  // Date Convention Handlers
  const handleAddDateConvention = () => {
    const newConvention: DateConvention = {
      id: Date.now().toString(),
      tradeType: TradeType.SPOT,
      currencyPair: '',
      defaultTradeDate: 'today',
      defaultValueDate: 'tradeDatePlus2',
      defaultExpiryDate: 'valueDateOption',
      defaultPaymentDate: 'valueDateOption',
      useBusinessDays: true,
      hasOverride: false,
    };
    setDateConventions(prev => [...prev, newConvention]);
  };

  const handleDeleteDateConvention = (id: string) => {
    setDateConventions(prev => prev.filter(c => c.id !== id));
    showNotification(t('tradeBooking.dates.deleted'), 'success');
  };

  const handleToggleOverride = (id: string) => {
    setDateConventions(prev =>
      prev.map(c =>
        c.id === id ? { ...c, hasOverride: !c.hasOverride } : c
      )
    );
  };

  const handleUpdateDateConvention = (id: string, field: string, value: any) => {
    setDateConventions(prev =>
      prev.map(c =>
        c.id === id ? { ...c, [field]: value } : c
      )
    );
  };

  const handleOpenDatesSettings = () => {
    // Save current state before opening modal
    setSavedDateConventions(JSON.parse(JSON.stringify(dateConventions)));
    setShowDatesSettings(true);
  };

  const handleCloseDatesSettings = () => {
    // Restore saved state on cancel
    setDateConventions(JSON.parse(JSON.stringify(savedDateConventions)));
    setShowDatesSettings(false);
  };

  const handleSaveDateConventions = async () => {
    // Validate: if override is enabled, all override fields must have values
    const invalidConventions = dateConventions.filter(convention => {
      if (!convention.hasOverride) return false;

      // For Forward trades, expiry date is not applicable
      if (convention.tradeType === TradeType.FORWARD) {
        return !convention.overrideTradeDate ||
               !convention.overrideValueDate ||
               !convention.overridePaymentDate;
      }

      // For Spot trades, all fields must be filled
      return !convention.overrideTradeDate ||
             !convention.overrideValueDate ||
             !convention.overrideExpiryDate ||
             !convention.overridePaymentDate;
    });

    if (invalidConventions.length > 0) {
      showNotification(t('tradeBooking.dates.validationError'), 'error');
      return;
    }

    // Build overrides object to save (only include conventions with overrides enabled)
    const overrides: Record<string, DateConventionOverride> = {};
    dateConventions.forEach((convention) => {
      if (convention.hasOverride) {
        overrides[convention.id] = {
          hasOverride: true,
          overrideTradeDate: convention.overrideTradeDate,
          overrideValueDate: convention.overrideValueDate,
          overrideExpiryDate: convention.overrideExpiryDate,
          overridePaymentDate: convention.overridePaymentDate,
        };
      } else {
        // Save with hasOverride: false to clear any previous overrides
        overrides[convention.id] = {
          hasOverride: false,
        };
      }
    });

    try {
      await saveDateConventionOverrides(overrides);
      setSavedDateConventions(JSON.parse(JSON.stringify(dateConventions)));
      showNotification(t('tradeBooking.dates.saved'), 'success');
      setShowDatesSettings(false);
    } catch (error) {
      console.error('Failed to save date conventions:', error);
      showNotification(t('tradeBooking.dates.saveFailed'), 'error');
    }
  };

  const handleResetDateDefaults = () => {
    // TODO: Reset to system defaults from backend
    showNotification(t('tradeBooking.dates.reset'), 'success');
  };

  const handleSaveFixingExpiryConventions = async (conventions: FixingExpiryConvention[]) => {
    // Validate conventions
    for (const convention of conventions) {
      // Get the active values (use override if enabled and exists, otherwise default)
      const activeFixingReference = convention.hasOverride && convention.overrideFixingReference
        ? convention.overrideFixingReference
        : convention.defaultFixingReference;
      const activeFixingOffset = convention.hasOverride && convention.overrideFixingOffset
        ? convention.overrideFixingOffset
        : convention.defaultFixingOffset;
      const activeSettlementType = convention.hasOverride && convention.overrideSettlementType
        ? convention.overrideSettlementType
        : convention.defaultSettlementType;
      const activeSettlementCurrency = convention.hasOverride && convention.overrideSettlementCurrency !== undefined
        ? convention.overrideSettlementCurrency
        : convention.defaultSettlementCurrency;

      // If override is enabled, check all override fields have values
      if (convention.hasOverride) {
        if (!convention.overrideFixingReference || !convention.overrideFixingOffset || !convention.overrideSettlementType) {
          showNotification(t('tradeBooking.fixingExpiry.validationError'), 'error');
          return;
        }
      }

      // Validate Settlement Currency based on Settlement Type
      if (activeSettlementType === SettlementType.ENTREGA_FISICA) {
        // Must be N/A (empty)
        if (activeSettlementCurrency && activeSettlementCurrency !== '') {
          showNotification(t('tradeBooking.fixingExpiry.entregaFisicaValidation'), 'error');
          return;
        }
      } else if (activeSettlementType === SettlementType.COMPENSACION) {
        // Cannot be N/A (must have value)
        if (!activeSettlementCurrency || activeSettlementCurrency === '') {
          showNotification(t('tradeBooking.fixingExpiry.compensacionValidation'), 'error');
          return;
        }
      }
    }

    // Build overrides object to save (only include conventions with overrides enabled)
    const overrides: Record<string, FixingExpiryConventionOverride> = {};
    conventions.forEach((convention) => {
      if (convention.hasOverride) {
        overrides[convention.id] = {
          hasOverride: true,
          overrideFixingReference: convention.overrideFixingReference,
          overrideFixingOffset: convention.overrideFixingOffset,
          overrideSettlementType: convention.overrideSettlementType,
          overrideSettlementCurrency: convention.overrideSettlementCurrency,
        };
      } else {
        // Save with hasOverride: false to clear any previous overrides
        overrides[convention.id] = {
          hasOverride: false,
        };
      }
    });

    try {
      await saveFixingExpiryConventionOverrides(overrides);
      setFixingExpiryConventions(conventions);
      showNotification(t('tradeBooking.fixingExpiry.saved'), 'success');
      setShowFixingSettings(false);
    } catch (error) {
      console.error('Failed to save fixing/expiry conventions:', error);
      showNotification(t('tradeBooking.fixingExpiry.saveFailed'), 'error');
    }
  };

  const handleMicrophoneMouseDown = async (shortcut: Shortcut) => {
    const shortcutKey = shortcut.id;
    if (micStates[shortcutKey] !== 'idle' && micStates[shortcutKey] !== undefined) return;

    // First, auto-fill the form with shortcut data
    handleShortcutClick(shortcut);

    try {
      // Start recording
      const recorder = await startRecording();
      setActiveRecorders(prev => ({ ...prev, [shortcutKey]: recorder }));

      // Set to listening state
      setMicStates(prev => ({ ...prev, [shortcutKey]: 'listening' }));
    } catch (error) {
      console.error('Failed to start recording:', error);
      setMicStates(prev => ({ ...prev, [shortcutKey]: 'error' }));
      showNotification(t('tradeBooking.voice.error'), 'error');

      setTimeout(() => {
        setMicStates(prev => ({ ...prev, [shortcutKey]: 'idle' }));
      }, 3000);
    }
  };

  const handleMicrophoneMouseUp = async (shortcut: Shortcut) => {
    const shortcutKey = shortcut.id;
    const recorder = activeRecorders[shortcutKey];

    if (!recorder || micStates[shortcutKey] !== 'listening') return;

    try {
      // Stop recording and get audio blob
      const audioBlob = await recorder.stop();

      // Remove recorder from state
      setActiveRecorders(prev => {
        const newRecorders = { ...prev };
        delete newRecorders[shortcutKey];
        return newRecorders;
      });

      // Set to processing state
      setMicStates(prev => ({ ...prev, [shortcutKey]: 'processing' }));

      // Map locale to language code
      const languageMap: Record<string, string> = {
        'es': 'es-CL',
        'en': 'en-US',
        'pt': 'pt-BR',
      };
      const languageCode = languageMap[locale] || 'es-CL';

      // Identify empty fields in Basic Data section
      const emptyFields: string[] = [];
      if (!form.tradeType) emptyFields.push('tradeType');
      if (!form.currency1) emptyFields.push('currency1');
      if (!form.currency2) emptyFields.push('currency2');
      if (!form.amount) emptyFields.push('amount');
      if (!form.price) emptyFields.push('price');
      if (!form.direction) emptyFields.push('direction');

      if (!form.ourParty) emptyFields.push('ourParty');
      if (!form.counterparty) emptyFields.push('counterparty');

      // Build field options context for dynamic prompt
      const fieldOptions = {
        currency1: currencies.map(c => c.code),
        currency2: currencies.map(c => c.code),
        ourParty: tradingEntities.map(e => e.name),
        counterparty: banks.map(b => b.name),
      };

      // TODO: Get system prompt from user (you'll provide this)
      const systemPrompt = undefined; // Will be set later

      // Transcribe audio and extract trade details
      const result = await transcribeAudio(audioBlob, languageCode, emptyFields, systemPrompt, fieldOptions);

      // Populate form fields with extracted data and track missed fields
      const extractedData = result.extracted_data || {};
      const stillEmpty: string[] = [];

      if (result.extracted_data) {

        // Update each field that was extracted, track what's still empty
        if (extractedData.tradeType !== undefined) {
          updateField('tradeType', extractedData.tradeType);
        } else if (emptyFields.includes('tradeType')) {
          stillEmpty.push('tradeType');
        }

        if (extractedData.direction !== undefined) {
          updateField('direction', extractedData.direction);
        } else if (emptyFields.includes('direction')) {
          stillEmpty.push('direction');
        }

        if (extractedData.amount !== undefined) {
          updateField('amount', extractedData.amount);
          setAmountInput(formatAmountDisplay(extractedData.amount, locale));
        } else if (emptyFields.includes('amount')) {
          stillEmpty.push('amount');
        }

        if (extractedData.price !== undefined) {
          updateField('price', extractedData.price);
          setPriceInput(formatPriceDisplay(extractedData.price, locale, 4));
        } else if (emptyFields.includes('price')) {
          stillEmpty.push('price');
        }

        if (extractedData.currency1 !== undefined) {
          updateField('currency1', extractedData.currency1);
        } else if (emptyFields.includes('currency1')) {
          stillEmpty.push('currency1');
        }

        if (extractedData.currency2 !== undefined) {
          updateField('currency2', extractedData.currency2);
        } else if (emptyFields.includes('currency2')) {
          stillEmpty.push('currency2');
        }

        if (extractedData.ourParty !== undefined) {
          const selectedEntity = tradingEntities.find(e => e.name === extractedData.ourParty);
          updateField('ourParty', extractedData.ourParty);
          updateField('ourPartyId', selectedEntity?.id || '');
          updateField('ourPartyTaxId', selectedEntity?.taxId || '');
        } else if (emptyFields.includes('ourParty')) {
          stillEmpty.push('ourParty');
        }

        if (extractedData.counterparty !== undefined) {
          const selectedBank = banks.find(b => b.name === extractedData.counterparty);
          updateField('counterparty', extractedData.counterparty);
          updateField('counterpartyId', selectedBank?.id || '');
          updateField('counterpartyTaxId', selectedBank?.taxId || '');
        } else if (emptyFields.includes('counterparty')) {
          stillEmpty.push('counterparty');
        }

        // Update missed fields state for yellow highlighting
        setMissedFields(stillEmpty);
      }

      // Set success state
      setMicStates(prev => ({ ...prev, [shortcutKey]: 'success' }));
      showNotification(t('tradeBooking.voice.success'), 'success');

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setMicStates(prev => ({ ...prev, [shortcutKey]: 'idle' }));
      }, 3000);

    } catch (error) {
      console.error('Speech-to-text error:', error);
      setMicStates(prev => ({ ...prev, [shortcutKey]: 'error' }));
      showNotification(t('tradeBooking.voice.error'), 'error');

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setMicStates(prev => ({ ...prev, [shortcutKey]: 'idle' }));
      }, 3000);
    }
  };

  const handleSectionMicrophoneClick = async (section: string) => {
    // Placeholder for sections not yet implemented
    showNotification(`Voice input for ${section} section coming soon!`, 'info');
  };

  const handleSectionMicrophoneMouseDown = async (section: string) => {
    if (sectionMicStates[section] !== 'idle') return;

    try {
      const recorder = await startRecording();
      setSectionRecorders(prev => ({ ...prev, [section]: recorder }));
      setSectionMicStates(prev => ({ ...prev, [section]: 'listening' }));
    } catch (error) {
      console.error('Failed to start recording:', error);
      showNotification(t('tradeBooking.voice.error'), 'error');
    }
  };

  const handleSectionMicrophoneMouseUp = async (section: string) => {
    const recorder = sectionRecorders[section];
    if (!recorder || sectionMicStates[section] !== 'listening') return;

    try {
      setSectionMicStates(prev => ({ ...prev, [section]: 'processing' }));

      const audioBlob = await recorder.stop();
      setSectionRecorders(prev => {
        const newRecorders = { ...prev };
        delete newRecorders[section];
        return newRecorders;
      });

      if (section === 'basicData') {
        // Build list of fields to extract for Basic Data section
        // ALWAYS include tradeType and direction (even if they have default values)
        const emptyFields: string[] = ['tradeType', 'direction'];

        // Add other fields only if they're actually empty
        if (!form.amount) emptyFields.push('amount');
        if (!form.price) emptyFields.push('price');
        if (!form.currency1) emptyFields.push('currency1');
        if (!form.currency2) emptyFields.push('currency2');
        if (!form.ourParty) emptyFields.push('ourParty');
        if (!form.counterparty) emptyFields.push('counterparty');

        // Build field options from available currencies and parties
        const fieldOptions = {
          currency1: currencies.map(c => c.code),
          currency2: currencies.map(c => c.code),
          ourParty: tradingEntities.map(e => e.name),
          counterparty: banks.map(b => b.name),
        };

        const languageCode = locale === 'es' ? 'es-CL' : locale === 'pt' ? 'pt-BR' : 'en-US';
        const result = await transcribeAudio(audioBlob, languageCode, emptyFields, undefined, fieldOptions);

        if (result.transcript) {
          const extractedData = result.extracted_data || {};

          // Track which fields were requested but not extracted
          const stillEmpty: string[] = [];

          // Populate extracted fields
          if (extractedData.tradeType !== undefined) {
            updateField('tradeType', extractedData.tradeType);
          } else {
            stillEmpty.push('tradeType');
          }

          if (extractedData.direction !== undefined) {
            updateField('direction', extractedData.direction);
          } else {
            stillEmpty.push('direction');
          }

          if (extractedData.amount !== undefined) {
            updateField('amount', extractedData.amount);
            setAmountInput(formatAmountDisplay(extractedData.amount, locale, 2));
          } else if (emptyFields.includes('amount')) {
            stillEmpty.push('amount');
          }

          if (extractedData.price !== undefined) {
            updateField('price', extractedData.price);
            setPriceInput(formatPriceDisplay(extractedData.price, locale, 4));
          } else if (emptyFields.includes('price')) {
            stillEmpty.push('price');
          }

          if (extractedData.currency1 !== undefined) {
            updateField('currency1', extractedData.currency1);
          } else if (emptyFields.includes('currency1')) {
            stillEmpty.push('currency1');
          }

          if (extractedData.currency2 !== undefined) {
            updateField('currency2', extractedData.currency2);
          } else if (emptyFields.includes('currency2')) {
            stillEmpty.push('currency2');
          }

          if (extractedData.ourParty !== undefined) {
            const selectedEntity = tradingEntities.find(e => e.name === extractedData.ourParty);
            updateField('ourParty', extractedData.ourParty);
            updateField('ourPartyId', selectedEntity?.id || '');
            updateField('ourPartyTaxId', selectedEntity?.taxId || '');
          } else if (emptyFields.includes('ourParty')) {
            stillEmpty.push('ourParty');
          }

          if (extractedData.counterparty !== undefined) {
            const selectedBank = banks.find(b => b.name === extractedData.counterparty);
            updateField('counterparty', extractedData.counterparty);
            updateField('counterpartyId', selectedBank?.id || '');
            updateField('counterpartyTaxId', selectedBank?.taxId || '');
          } else if (emptyFields.includes('counterparty')) {
            stillEmpty.push('counterparty');
          }

          setMissedFields(stillEmpty);

          setSectionMicStates(prev => ({ ...prev, [section]: 'success' }));
          showNotification(t('tradeBooking.voice.success'), 'success');
        } else {
          setSectionMicStates(prev => ({ ...prev, [section]: 'error' }));
          showNotification(t('tradeBooking.voice.error'), 'error');
        }
      } else if (section === 'dates') {
        // Build list of date fields - all can be overwritten
        const emptyFields: string[] = [];

        // Always include ALL date fields regardless of trade type
        // User may mention any dates, and we'll extract whatever they say
        emptyFields.push('tradeDate', 'valueDate', 'expiryDate', 'paymentDate', 'maturity');

        const languageCode = locale === 'es' ? 'es-CL' : locale === 'pt' ? 'pt-BR' : 'en-US';
        const result = await transcribeAudio(audioBlob, languageCode, emptyFields);

        if (result.transcript) {
          const extractedData = result.extracted_data || {};
          const stillEmpty: string[] = [];

          // Populate extracted date fields
          if (extractedData.tradeDate !== undefined) {
            const parsedDate = parseDateFromInput(extractedData.tradeDate);
            if (parsedDate) {
              updateField('tradeDate', parsedDate);
            }
          } else {
            stillEmpty.push('tradeDate');
          }

          if (extractedData.valueDate !== undefined) {
            const parsedDate = parseDateFromInput(extractedData.valueDate);
            if (parsedDate) {
              updateField('valueDate', parsedDate);
            }
          } else if (emptyFields.includes('valueDate')) {
            stillEmpty.push('valueDate');
          }

          if (extractedData.expiryDate !== undefined) {
            const parsedDate = parseDateFromInput(extractedData.expiryDate);
            if (parsedDate) {
              updateField('expiryDate', parsedDate);
            }
          } else if (emptyFields.includes('expiryDate')) {
            stillEmpty.push('expiryDate');
          }

          if (extractedData.paymentDate !== undefined) {
            const parsedDate = parseDateFromInput(extractedData.paymentDate);
            if (parsedDate) {
              updateField('paymentDate', parsedDate);
            }
          } else if (emptyFields.includes('paymentDate')) {
            stillEmpty.push('paymentDate');
          }

          if (extractedData.maturity !== undefined) {
            // Use handleMaturityChange which calculates expiryDate from valueDate + maturity
            handleMaturityChange(extractedData.maturity);
          } else if (emptyFields.includes('maturity')) {
            stillEmpty.push('maturity');
          }

          setMissedFields(stillEmpty);

          setSectionMicStates(prev => ({ ...prev, [section]: 'success' }));
          showNotification(t('tradeBooking.voice.success'), 'success');
        } else {
          setSectionMicStates(prev => ({ ...prev, [section]: 'error' }));
          showNotification(t('tradeBooking.voice.error'), 'error');
        }
      } else if (section === 'fixing') {
        // Build list of fixing fields - all can be overwritten
        const emptyFields: string[] = ['fixingReference', 'fixingOffset', 'settlementType', 'settlementCurrency'];

        // Get available options from enums
        const fieldOptions = {
          fixingReference: Object.values(FixingReference),
          fixingOffset: Object.values(FixingOffset),
          settlementType: Object.values(SettlementType),
          settlementCurrency: currencies.map(c => c.code),
        };

        const languageCode = locale === 'es' ? 'es-CL' : locale === 'pt' ? 'pt-BR' : 'en-US';
        const result = await transcribeAudio(audioBlob, languageCode, emptyFields, undefined, fieldOptions);

        if (result.transcript) {
          const extractedData = result.extracted_data || {};
          const stillEmpty: string[] = [];

          // Populate extracted fixing fields
          if (extractedData.fixingReference !== undefined) {
            updateField('fixingReference', extractedData.fixingReference);
          } else {
            stillEmpty.push('fixingReference');
          }

          if (extractedData.fixingOffset !== undefined) {
            updateField('fixingOffset', extractedData.fixingOffset);
          } else {
            stillEmpty.push('fixingOffset');
          }

          if (extractedData.settlementType !== undefined) {
            handleSettlementTypeChange(extractedData.settlementType);
            // If ENTREGA_FISICA, automatically set settlementCurrency to N/A
            if (extractedData.settlementType === SettlementType.ENTREGA_FISICA) {
              updateField('settlementCurrency', 'N/A');
            }
          } else {
            stillEmpty.push('settlementType');
          }

          if (extractedData.settlementCurrency !== undefined && extractedData.settlementType !== SettlementType.ENTREGA_FISICA) {
            // Only set settlementCurrency if NOT ENTREGA_FISICA (otherwise it's already N/A)
            updateField('settlementCurrency', extractedData.settlementCurrency);
          } else if (emptyFields.includes('settlementCurrency') && extractedData.settlementType !== SettlementType.ENTREGA_FISICA) {
            stillEmpty.push('settlementCurrency');
          }

          setMissedFields(stillEmpty);

          setSectionMicStates(prev => ({ ...prev, [section]: 'success' }));
          showNotification(t('tradeBooking.voice.success'), 'success');
        } else {
          setSectionMicStates(prev => ({ ...prev, [section]: 'error' }));
          showNotification(t('tradeBooking.voice.error'), 'error');
        }
      } else if (section === 'notes') {
        // Notes section: just transcribe and paste into notes field
        const languageCode = locale === 'es' ? 'es-CL' : locale === 'pt' ? 'pt-BR' : 'en-US';
        const result = await transcribeAudio(audioBlob, languageCode);

        if (result.transcript) {
          // Append to existing notes (or replace if empty)
          const currentNotes = form.notes || '';
          const newNotes = currentNotes ? `${currentNotes}\n${result.transcript}` : result.transcript;
          updateField('notes', newNotes);

          setSectionMicStates(prev => ({ ...prev, [section]: 'success' }));
          showNotification(t('tradeBooking.voice.success'), 'success');
        } else {
          setSectionMicStates(prev => ({ ...prev, [section]: 'error' }));
          showNotification(t('tradeBooking.voice.error'), 'error');
        }
      }

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setSectionMicStates(prev => ({ ...prev, [section]: 'idle' }));
      }, 3000);

    } catch (error) {
      console.error('Section microphone error:', error);
      setSectionMicStates(prev => ({ ...prev, [section]: 'error' }));
      showNotification(t('tradeBooking.voice.error'), 'error');

      setTimeout(() => {
        setSectionMicStates(prev => ({ ...prev, [section]: 'idle' }));
      }, 3000);
    }
  };

  const getMicrophoneIcon = (state: string) => {
    switch (state) {
      case 'listening':
      case 'idle':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1.5">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" fill="none"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        );
      case 'processing':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1.5">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" fill="none"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        );
      case 'success':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        );
      default:
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1.5">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" fill="none"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        );
    }
  };

  const handleConfirm = async (typeOverride?: 'draft' | 'submit' | 'approval' | 'update' | 'book_draft') => {
    setShowConfirmation(false);
    setIsLoading(true);
    const actionType = (typeof typeOverride === 'string' ? typeOverride : undefined) ?? confirmationType;

    try {
      // Use ourPartyId as the client ID for the API route
      const tradingEntityId = form.ourPartyId;

      if (!tradingEntityId) {
        showNotification('Please select a trading entity', 'error');
        setIsLoading(false);
        return;
      }

      if (actionType === 'update') {
        // Update existing trade — use the original client ID (where the trade doc lives),
        // not the current form OurPartyId which may have been changed by the user.
        const updateClientId = editingTradeClientId || tradingEntityId;
        const result = await updateTrade(form, updateClientId, editingTradeId!);
        showNotification(t('tradeBooking.messages.tradeUpdated'), 'success');
        // Refresh grid — the onRowSelectedRef effect will re-populate form with fresh data
        setGridRefreshTrigger(prev => prev + 1);

        // KAN-237: Check for potential confirmation match after update
        if (result.potentialConfirmationMatch) {
          if (result.potentialConfirmationMatch.type === 'single') {
            setPendingConfirmationMatch(result.potentialConfirmationMatch);
          } else if (result.potentialConfirmationMatch.type === 'multiple') {
            showNotification(t('tradeBooking.messages.confirmationMatchMultiple', { count: result.potentialConfirmationMatch.matchCount }), 'info');
          }
        }
      } else if (actionType === 'book_draft') {
        // Promote draft trade to Booked — use original client ID (same reason as update)
        const bookClientId = editingTradeClientId || tradingEntityId;
        const result = await bookDraft(form, bookClientId, editingTradeId!, allocationRows.length > 0 ? allocationRows : undefined);
        const baseMessage = canDirectlyBook
          ? t('tradeBooking.messages.tradeSubmitted')
          : t('tradeBooking.messages.submittedForApproval');

        const si = result.settlementInstruction;
        if (si?.attempted && si.success) {
          showNotification(`${baseMessage}. ${t('tradeBooking.messages.settlementInstructionSent', { email: si.toEmail })}`, 'success');
        } else if (si?.attempted && !si.success) {
          showNotification(baseMessage, 'success');
          setTimeout(() => showNotification(t('tradeBooking.messages.settlementInstructionFailed', { error: si.errorMessage }), 'warning'), 4500);
        } else {
          showNotification(baseMessage, 'success');
        }

        // KAN-216: Check for potential confirmation match
        if (result.potentialConfirmationMatch) {
          if (result.potentialConfirmationMatch.type === 'single') {
            setPendingConfirmationMatch(result.potentialConfirmationMatch);
          } else if (result.potentialConfirmationMatch.type === 'multiple') {
            showNotification(t('tradeBooking.messages.confirmationMatchMultiple', { count: result.potentialConfirmationMatch.matchCount }), 'info');
          }
        }

        resetForm();
        setActiveQuote(null);
        setAllocationRows([]);

        if (result.tradeId) {
          setNewlyCreatedTradeId(result.tradeId);
          setGridRefreshTrigger(prev => prev + 1);
        }
      } else if (actionType === 'draft') {
        const result = await saveDraft(form, tradingEntityId, activeQuote?.quoteId);
        showNotification(t('tradeBooking.messages.draftSaved'), 'success');
        resetForm();
        setActiveQuote(null); // Clear any active quote

        // Trigger grid refresh and select the new trade
        if (result.tradeId) {
          setNewlyCreatedTradeId(result.tradeId);
          setGridRefreshTrigger(prev => prev + 1);
        }
      } else {
        const result = await submitTrade(form, tradingEntityId, activeQuote?.quoteId, allocationRows.length > 0 ? allocationRows : undefined);
        const baseMessage = canDirectlyBook
          ? t('tradeBooking.messages.tradeSubmitted')
          : t('tradeBooking.messages.submittedForApproval');

        // Phase 7: Settlement instruction toast
        const si = result.settlementInstruction;
        if (si?.attempted && si.success) {
          showNotification(`${baseMessage}. ${t('tradeBooking.messages.settlementInstructionSent', { email: si.toEmail })}`, 'success');
        } else if (si?.attempted && !si.success) {
          showNotification(baseMessage, 'success');
          // Show warning after success toast finishes (4s duration + 300ms slide-out)
          setTimeout(() => showNotification(t('tradeBooking.messages.settlementInstructionFailed', { error: si.errorMessage }), 'warning'), 4500);
        } else {
          showNotification(baseMessage, 'success');
        }

        // KAN-216: Check for potential confirmation match
        if (result.potentialConfirmationMatch) {
          if (result.potentialConfirmationMatch.type === 'single') {
            setPendingConfirmationMatch(result.potentialConfirmationMatch);
          } else if (result.potentialConfirmationMatch.type === 'multiple') {
            showNotification(t('tradeBooking.messages.confirmationMatchMultiple', { count: result.potentialConfirmationMatch.matchCount }), 'info');
          }
        }

        resetForm();
        setActiveQuote(null); // Clear any active quote
        setAllocationRows([]); // Clear allocation

        // Trigger grid refresh and select the new trade
        if (result.tradeId) {
          setNewlyCreatedTradeId(result.tradeId);
          setGridRefreshTrigger(prev => prev + 1);
        }
      }
    } catch (error) {
      // Map known backend errors to locale-specific translations
      const rawMessage = error instanceof Error ? error.message : '';
      const knownErrors: Record<string, string> = {
        'Cannot change amount on a trade with allocations': t('tradeBooking.messages.cannotChangeAllocatedAmount'),
      };
      const matchedKey = Object.keys(knownErrors).find(key => rawMessage.includes(key));
      const errorMessage = matchedKey ? knownErrors[matchedKey] : (rawMessage || t('tradeBooking.messages.error'));
      showNotification(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper: get grouping values filtered by the currently selected entity
  const getFilteredGroupingValues = (groupingNumber: 1 | 2 | 3) => {
    if (!groupingsConfig || !form.ourPartyId) return [];
    const entityMapping = groupingsConfig.entityMappings.find(m => m.entityId === form.ourPartyId);
    if (!entityMapping) return [];

    if (groupingNumber === 1) {
      return groupingsConfig.grouping1Values.filter(v => v.isActive && entityMapping.grouping1ValueIds.includes(v.id));
    }

    if (groupingNumber === 2) {
      // G2: in subgrouping mode, values depend on the selected G1
      if (groupingsConfig.grouping2Mode === 'subgrouping') {
        const selectedG1 = groupingsConfig.grouping1Values.find(v => v.name === form.grouping1);
        if (!selectedG1 || !entityMapping.g1ToG2Mappings) return [];
        const allowedIds = entityMapping.g1ToG2Mappings[selectedG1.id] || [];
        return groupingsConfig.grouping2Values.filter(v => v.isActive && allowedIds.includes(v.id));
      }
      // Parallel mode: G2 values are independent
      return groupingsConfig.grouping2Values.filter(v => v.isActive && entityMapping.grouping2ValueIds.includes(v.id));
    }

    // G3
    if (groupingsConfig.grouping3Mode === 'subgrouping') {
      const selectedG2 = groupingsConfig.grouping2Values.find(v => v.name === form.grouping2);
      if (!selectedG2 || !entityMapping.g2ToG3Mappings) return [];
      const allowedIds = entityMapping.g2ToG3Mappings[selectedG2.id] || [];
      return groupingsConfig.grouping3Values.filter(v => v.isActive && allowedIds.includes(v.id));
    }
    // Parallel mode: G3 values are independent
    return groupingsConfig.grouping3Values.filter(v => v.isActive && entityMapping.grouping3ValueIds.includes(v.id));
  };

  const tradeStatus = editingWorkflowState || 'Draft';

  // Editable = Draft or Booked trade selected from grid; ReadOnly = Confirmed+ trade selected
  const isEditable = isViewingTrade && !!editingTradeId && ['Draft', 'Booked'].includes(editingWorkflowState || '');
  const isReadOnly = isViewingTrade && !isEditable;

  return (
    <div className="trade-booking-page">
      {/* Header with title and FXGO corrections bell */}
      <div className="booking-header">
        <div className="booking-title">{t('tradeBooking.title')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {dateFrom !== null && (
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo || ''}
              onApply={(from, to) => { setDateFrom(from); setDateTo(to); }}
            />
          )}
          <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
          <button
            className="fxgo-corrections-bell-btn"
            onClick={() => setShowFxgoCorrectionsModal(true)}
            title={t('fxgoCorrections.title')}
          >
            <Bell size={20} />
            {fxgoCorrectionsCount > 0 && (
              <span className="fxgo-corrections-badge">{fxgoCorrectionsCount}</span>
            )}
          </button>
        </div>
      </div>

      <PanelGroup
        orientation="vertical"
        className="trade-booking-panel-group"
      >
        <Panel defaultSize={60} minSize={30}>
          <div className="trade-booking-form-wrapper">
            {isLoading && (
              <div className="trade-submit-overlay">
                <div className="trade-submit-spinner" />
                <p>{t('tradeBooking.messages.loading')}</p>
              </div>
            )}
            <div className={`trade-booking-form${isExtracting ? ' extracting' : ''}${isVoiceProcessing ? ' voice-processing' : ''}${isLoading ? ' submitting' : ''}`}>
              {/* Status Badge */}
        <div className={`trade-status ${tradeStatus.toLowerCase()}`}>
          {tradeStatus}
        </div>

        {/* Shortcuts Section */}
        <div className="form-section shortcuts-section">
          <div className="section-title">
            {t('tradeBooking.shortcuts.title')}
            <button type="button" className="settings-icon" onClick={() => setShowShortcutsSettings(true)}>⚙️</button>
          </div>

          {loadingShortcuts ? (
            <div className="section-loading"><div className="section-spinner" /></div>
          ) : (
          <>
          {/* Dynamically render shortcuts */}
          {shortcuts.map((shortcut) => (
            <div key={shortcut.id} className="shortcut-row">
              <button
                type="button"
                className="btn-shortcut"
                onClick={() => handleShortcutClick(shortcut)}
              >
                {shortcut.name}
              </button>
              <button
                type="button"
                className={`btn-microphone-animated ${micStates[shortcut.id] || 'idle'}`}
                disabled={isVoiceActive && (micStates[shortcut.id] || 'idle') === 'idle'}
                onMouseDown={() => handleMicrophoneMouseDown(shortcut)}
                onMouseUp={() => handleMicrophoneMouseUp(shortcut)}
                onMouseLeave={() => handleMicrophoneMouseUp(shortcut)}
                onTouchStart={() => handleMicrophoneMouseDown(shortcut)}
                onTouchEnd={() => handleMicrophoneMouseUp(shortcut)}
              >
                {getMicrophoneIcon(micStates[shortcut.id] || 'idle')}
              </button>
            </div>
          ))}
          </>
          )}
        </div>

        {/* Section 1: Core Trade Details */}
        <div className="form-section">
          {/* Section title with capture zone on the right */}
          <div className="section-header">
            <div className="section-title-with-mic">
              <div className="section-title">{t('tradeBooking.sections.basicData')}</div>
              <button
                type="button"
                className={`btn-microphone-animated ${sectionMicStates.basicData}`}
                disabled={isVoiceActive && sectionMicStates.basicData === 'idle'}
                onMouseDown={() => handleSectionMicrophoneMouseDown('basicData')}
                onMouseUp={() => handleSectionMicrophoneMouseUp('basicData')}
                onMouseLeave={() => {
                  if (sectionMicStates.basicData === 'listening') {
                    handleSectionMicrophoneMouseUp('basicData');
                  }
                }}
                onTouchStart={() => handleSectionMicrophoneMouseDown('basicData')}
                onTouchEnd={() => handleSectionMicrophoneMouseUp('basicData')}
              >
                {getMicrophoneIcon(sectionMicStates.basicData)}
              </button>
            </div>
            {/* Trade Image Capture Zone (KAN-82) + Multi-Trade button */}
            <div className="section-header-right">
              {user && (
                <TradeImageCapturePanel
                  clientId={user.organization?.id || user.id}
                  onExtractionStart={() => setIsExtracting(true)}
                  onExtractionEnd={() => setIsExtracting(false)}
                  onExtractionComplete={handleExtractionComplete}
                  onMultiTradeDetected={handleMultiTradeDetected}
                  onClear={clearAllConfidence}
                  disabled={isExtracting || isLoading || isVoiceActive}
                  resetTrigger={captureResetTrigger}
                />
              )}
              <button
                type="button"
                className="btn-microphone-animated"
                onClick={() => setMultiTradeModalOpen(true)}
                onContextMenu={handleLayersContextMenu}
                disabled={isLoading || isVoiceActive}
                title={t('multiTrade.tradeGridButton')}
              >
                <Layers size={16} />
              </button>
            </div>
          </div>

          {loadingCoreData ? (
            <div className="section-loading"><div className="section-spinner" /></div>
          ) : (
          <>
          {/* Product (Trade Type) */}
          <div className={`form-group ${getConfidenceClass('tradeType')}`}>
            <label className="form-label">{t('tradeBooking.tradeType.label')}{renderConfidenceIndicator('tradeType')}</label>
            <div style={form.tradeType === TradeType.FORWARD && !form.isAverageRateForward ? { display: 'flex', gap: '0.5rem', alignItems: 'center' } : undefined} className={!(form.tradeType === TradeType.FORWARD && !form.isAverageRateForward) ? 'button-group' : undefined}>
              <button
                type="button"
                className={`btn-option ${form.tradeType === TradeType.SPOT ? 'active' : ''}`}
                onClick={() => handleTradeTypeChange(TradeType.SPOT)}
                disabled={isReadOnly}
                style={form.tradeType === TradeType.FORWARD && !form.isAverageRateForward ? { flex: 1, height: '40px' } : undefined}
              >
                {t('tradeBooking.tradeType.spot')}
              </button>
              <button
                type="button"
                className={`btn-option ${form.tradeType === TradeType.FORWARD ? 'active' : ''}`}
                onClick={() => handleTradeTypeChange(TradeType.FORWARD)}
                disabled={isReadOnly}
                style={form.tradeType === TradeType.FORWARD && !form.isAverageRateForward ? { flex: 1, height: '40px' } : undefined}
              >
                {t('tradeBooking.tradeType.forward')}
              </button>
              {form.tradeType === TradeType.FORWARD && !form.isAverageRateForward && (
                <>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{t('tradeBooking.forwardStarting.toggle')}</span>
                  <label className="toggle-switch" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={form.isForwardStarting}
                      onChange={(e) => handleForwardStartingToggle(e.target.checked)}
                      disabled={isReadOnly}
                    />
                    <span className="slider"></span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Direction */}
          <div className={`form-group ${getConfidenceClass('direction')}`}>
            <label className="form-label">{t('tradeBooking.tradeDetails.direction')}{renderConfidenceIndicator('direction')}</label>
            <div className="button-group">
              <button
                type="button"
                className={`btn-option buy ${form.direction === Direction.BUY ? 'active' : ''}`}
                onClick={() => updateField('direction', Direction.BUY)}
                disabled={isReadOnly}
              >
                {t('tradeBooking.tradeDetails.buy')}
              </button>
              <button
                type="button"
                className={`btn-option sell ${form.direction === Direction.SELL ? 'active' : ''}`}
                onClick={() => updateField('direction', Direction.SELL)}
                disabled={isReadOnly}
              >
                {t('tradeBooking.tradeDetails.sell')}
              </button>
            </div>
          </div>

          {/* Currency 1, Currency 2 */}
          <div className="form-row">
            <div className={`form-group ${getConfidenceClass('currency1')}`}>
              <label className="form-label">{t('tradeBooking.tradeDetails.currency1')} *{renderConfidenceIndicator('currency1')}</label>
              <select
                className={`form-select ${errors.currency1 ? 'error' : ''} ${missedFields.includes('currency1') ? 'missed' : ''}`}
                value={form.currency1}
                onChange={(e) => {
                  const newCcy1 = e.target.value;
                  updateField('currency1', newCcy1);
                  setMissedFields(prev => prev.filter(f => f !== 'currency1'));
                  // Clear currency2 if it's no longer a valid pair
                  if (form.currency2) {
                    const validOptions = getValidCurrency2Options(newCcy1);
                    if (!validOptions.some(c => c.code === form.currency2)) {
                      updateField('currency2', '');
                    }
                  }
                }}
                disabled={isReadOnly}
              >
                <option value="">{t('tradeBooking.tradeDetails.selectCurrency')}</option>
                {getCurrency1Options(form.tradeType).map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code}
                  </option>
                ))}
              </select>
            </div>

            <div className={`form-group ${getConfidenceClass('currency2')}`}>
              <label className="form-label">{t('tradeBooking.tradeDetails.currency2')} *{renderConfidenceIndicator('currency2')}</label>
              <select
                className={`form-select ${errors.currency2 ? 'error' : ''} ${missedFields.includes('currency2') ? 'missed' : ''}`}
                value={form.currency2}
                onChange={(e) => {
                  updateField('currency2', e.target.value);
                  setMissedFields(prev => prev.filter(f => f !== 'currency2'));
                }}
                disabled={isReadOnly}
              >
                <option value="">{t('tradeBooking.tradeDetails.selectCurrency')}</option>
                {getValidCurrency2Options(form.currency1).map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {form.isForwardStarting ? (
            <>
              {/* Forward Starting: 3-column grid layout */}
              <div className="forward-starting-grid">
                {/* Row 1, Col 1: Amount */}
                <div className={`form-group ${getConfidenceClass('amount')}`} style={{ gridColumn: '1', gridRow: '1' }}>
                  <label className="form-label">{t('tradeBooking.tradeDetails.amount')} *{renderConfidenceIndicator('amount')}</label>
                  <input
                    type="text"
                    className={`form-input ${errors.amount ? 'error' : ''} ${missedFields.includes('amount') ? 'missed' : ''}`}
                    value={amountInput}
                    onChange={(e) => {
                      setAmountInput(e.target.value);
                      setMissedFields(prev => prev.filter(f => f !== 'amount'));
                    }}
                    onBlur={handleAmountBlur}
                    placeholder=""
                    disabled={isReadOnly}
                  />
                </div>
                {/* Row 1, Col 2: Spot Fixing Reference dropdown (Forward Starting) */}
                <div className="form-group" style={{ gridColumn: '2', gridRow: '1' }}>
                  <label className="form-label">{t('tradeBooking.tradeDetails.spotPrice')}</label>
                  <select
                    className="form-select"
                    value={form.spotFixingReference}
                    onChange={(e) => updateField('spotFixingReference', e.target.value as FixingReference)}
                    disabled={isReadOnly}
                  >
                    {Object.values(FixingReference).filter(ref => ref !== FixingReference.NA).map((ref) => (
                      <option key={ref} value={ref}>
                        {t(`tradeBooking.fixingReferences.${ref}`)}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Row 1, Col 3: Ptos Fwd + calendar icon + optional single chip */}
                <div className="form-group" style={{ gridColumn: '3', gridRow: '1' }}>
                  {/* Label row: Ptos Fwd label + calendar icon pushed right */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label className="form-label" style={{ marginBottom: 0 }}>Ptos Fwd *</label>
                    <FixingSchedulePanel
                      schedule={form.forwardStartingFixingSchedule}
                      tradeDate={form.tradeDate
                        ? `${form.tradeDate.getFullYear()}-${String(form.tradeDate.getMonth() + 1).padStart(2, '0')}-${String(form.tradeDate.getDate()).padStart(2, '0')}`
                        : ''}
                      maturityDate={form.expiryDate
                        ? `${form.expiryDate.getFullYear()}-${String(form.expiryDate.getMonth() + 1).padStart(2, '0')}-${String(form.expiryDate.getDate()).padStart(2, '0')}`
                        : ''}
                      onAddDates={handleForwardStartingScheduleAddDates}
                      onRemoveDate={handleForwardStartingScheduleRemoveDate}
                      onClearAll={handleForwardStartingScheduleClearAll}
                      disabled={isReadOnly}
                      minDates={1}
                      triggerOnly
                    />
                  </div>
                  {/* Input row: input + single date chip (if exactly 1 date) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="text"
                      className={`form-input ${errors.forwardPoints ? 'error' : ''}`}
                      value={forwardPointsInput}
                      onChange={(e) => setForwardPointsInput(e.target.value)}
                      onBlur={handleForwardPointsBlur}
                      placeholder=""
                      disabled={isReadOnly}
                    />
                    {form.forwardStartingFixingSchedule.length === 1 && form.forwardStartingFixingSchedule[0].date && (
                      <button
                        type="button"
                        className="fixing-date-chip"
                        onClick={() => !isReadOnly && handleForwardStartingScheduleRemoveDate(form.forwardStartingFixingSchedule[0].date)}
                        disabled={isReadOnly}
                        style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                      >
                        {(() => { const [, m, d] = form.forwardStartingFixingSchedule[0].date.split('-'); return `${d}/${m}`; })()}
                        <span className="chip-remove">&times;</span>
                      </button>
                    )}
                  </div>
                  {errors.forwardStartingFixingSchedule && (
                    <span className="error-message" style={{ fontSize: '0.7rem' }}>{t(errors.forwardStartingFixingSchedule)}</span>
                  )}
                </div>
                {/* Multi-date row: only appears when 2+ dates selected */}
                {form.forwardStartingFixingSchedule.length > 1 && (
                  <div style={{ gridColumn: '1 / 4', gridRow: '2' }}>
                    <div className="fixing-date-chips" style={{ justifyContent: 'flex-start' }}>
                      {[...form.forwardStartingFixingSchedule]
                        .filter(e => e.date)
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map((entry) => (
                          <button
                            key={entry.date}
                            type="button"
                            className="fixing-date-chip"
                            onClick={() => !isReadOnly && handleForwardStartingScheduleRemoveDate(entry.date)}
                            disabled={isReadOnly}
                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                          >
                            {(() => { const [, m, d] = entry.date.split('-'); return `${d}/${m}`; })()}
                            <span className="chip-remove">&times;</span>
                          </button>
                        ))}
                      {!isReadOnly && (
                        <button
                          type="button"
                          className="fixing-clear-all"
                          onClick={handleForwardStartingScheduleClearAll}
                          style={{ fontSize: '0.65rem' }}
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Price + Contra Amount below the grid */}
              <div className="form-row">
                <div className={`form-group ${getConfidenceClass('price')}`}>
                  <label className="form-label">{t('tradeBooking.tradeDetails.price')} *{renderConfidenceIndicator('price')}</label>
                  <input
                    type="text"
                    className="form-input readonly"
                    value={t('tradeBooking.forwardStarting.toBeFixed')}
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('tradeBooking.tradeDetails.contraAmount')}</label>
                  <input
                    type="text"
                    className="form-input readonly"
                    value={form.contraAmount !== null && form.contraAmount !== 0 ? formatAmountDisplay(form.contraAmount, locale, 2) : t('tradeBooking.forwardStarting.toBeFixed')}
                    readOnly
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Standard layout: Amount + Price */}
              <div className="form-row">
                <div className={`form-group ${getConfidenceClass('amount')}`}>
                  <label className="form-label">{t('tradeBooking.tradeDetails.amount')} *{renderConfidenceIndicator('amount')}</label>
                  <input
                    type="text"
                    className={`form-input ${errors.amount ? 'error' : ''} ${missedFields.includes('amount') ? 'missed' : ''}`}
                    value={amountInput}
                    onChange={(e) => {
                      setAmountInput(e.target.value);
                      setMissedFields(prev => prev.filter(f => f !== 'amount'));
                    }}
                    onBlur={handleAmountBlur}
                    placeholder=""
                    disabled={isReadOnly}
                  />
                </div>

                <div className={`form-group ${getConfidenceClass('price')}`}>
                  <label className="form-label">{t('tradeBooking.tradeDetails.price')} *{renderConfidenceIndicator('price')}</label>
                  <input
                    type="text"
                    className={`form-input ${errors.price ? 'error' : ''} ${missedFields.includes('price') ? 'missed' : ''}`}
                    value={priceInput}
                    onChange={(e) => {
                      setPriceInput(e.target.value);
                      setMissedFields(prev => prev.filter(f => f !== 'price'));
                    }}
                    onBlur={handlePriceBlur}
                    placeholder=""
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              {/* Forward Rate Breakdown */}
              {form.tradeType === TradeType.FORWARD && (
                <div className="rate-breakdown-section">
                  <div className="rate-breakdown-field">
                    <label className="rate-breakdown-label">{t('tradeBooking.tradeDetails.spotPrice')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={spotPriceInput}
                      onChange={(e) => setSpotPriceInput(e.target.value)}
                      onBlur={handleSpotPriceBlur}
                      placeholder=""
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="rate-breakdown-field">
                    <label className="rate-breakdown-label">{t('tradeBooking.tradeDetails.forwardPoints')}</label>
                    <input
                      type="text"
                      className={`form-input ${errors.forwardPoints ? 'error' : ''}`}
                      value={forwardPointsInput}
                      onChange={(e) => setForwardPointsInput(e.target.value)}
                      onBlur={handleForwardPointsBlur}
                      placeholder=""
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              )}

              {/* Contra Amount */}
              <div className="form-group">
                <label className="form-label">{t('tradeBooking.tradeDetails.contraAmount')}</label>
                <input
                  type="text"
                  className={`form-input readonly ${errors.contraAmount ? 'error' : ''}`}
                  value={form.contraAmount !== null ? formatAmountDisplay(form.contraAmount, locale, 2) : ''}
                  readOnly
                />
              </div>
            </>
          )}

          {/* Our Party and Counterparty */}
          <div className="form-row">
            <div className={`form-group ${getConfidenceClass('ourParty')}`}>
              <label className="form-label">{t('tradeBooking.tradeDetails.ourParty')} *{renderConfidenceIndicator('ourParty')}</label>
              <select
                className={`form-select ${errors.ourParty ? 'error' : ''} ${missedFields.includes('ourParty') ? 'missed' : ''}`}
                value={form.ourParty || ''}
                onChange={(e) => {
                  const selectedEntityName = e.target.value;
                  const selectedEntity = tradingEntities.find(ent => ent.name === selectedEntityName);
                  updateField('ourParty', selectedEntityName);
                  updateField('ourPartyId', selectedEntity?.id || '');
                  updateField('ourPartyTaxId', selectedEntity?.taxId || '');
                  updateField('grouping1', '');
                  updateField('grouping2', '');
                  updateField('grouping3', '');
                  setMissedFields(prev => prev.filter(f => f !== 'ourParty'));
                }}
                disabled={isReadOnly}
              >
                <option value="">{t('tradeBooking.tradeDetails.selectParty')}</option>
                {tradingEntities.map((entity) => (
                  <option key={entity.id} value={entity.name}>
                    {entity.isParent ? entity.name : `  └─ ${entity.name}`}
                  </option>
                ))}
              </select>
            </div>

            <div className={`form-group ${getConfidenceClass('counterparty')}`}>
              <label className="form-label">{t('tradeBooking.tradeDetails.counterparty')} *{renderConfidenceIndicator('counterparty')}</label>
              <select
                className={`form-select ${errors.counterparty ? 'error' : ''} ${missedFields.includes('counterparty') ? 'missed' : ''}`}
                value={form.counterparty || ''}
                onChange={(e) => {
                  const selectedBankName = e.target.value;
                  const selectedBank = banks.find(b => b.name === selectedBankName);
                  updateField('counterparty', selectedBankName);
                  updateField('counterpartyId', selectedBank?.id || '');
                  updateField('counterpartyTaxId', selectedBank?.taxId || '');
                  setMissedFields(prev => prev.filter(f => f !== 'counterparty'));
                  // Reload business calendar with union of client + counterparty centres
                  const centers = ['SCL'];
                  if (selectedBank?.businessCenter && selectedBank.businessCenter !== 'SCL') {
                    centers.push(selectedBank.businessCenter);
                  }
                  businessCalendar.loadFromAPI(centers).then(() => {
                    setCalendarVersion(v => v + 1);
                  });
                }}
                disabled={isReadOnly}
              >
                <option value="">{t('tradeBooking.tradeDetails.selectParty')}</option>
                {banks.map(bank => (
                  <option key={bank.id} value={bank.name}>{bank.name}</option>
                ))}
              </select>
            </div>
          </div>

          </>
          )}
        </div>

        {/* Section 2: Dates */}
        <div className="form-section">
          <div className="section-title">
            <div className="section-title-with-mic">
              <span>{t('tradeBooking.sections.dates')}</span>
              <button
                type="button"
                className={`btn-microphone-animated ${sectionMicStates.dates}`}
                disabled={isVoiceActive && sectionMicStates.dates === 'idle'}
                onMouseDown={() => handleSectionMicrophoneMouseDown('dates')}
                onMouseUp={() => handleSectionMicrophoneMouseUp('dates')}
                onMouseLeave={() => {
                  if (sectionMicStates.dates === 'listening') {
                    handleSectionMicrophoneMouseUp('dates');
                  }
                }}
                onTouchStart={() => handleSectionMicrophoneMouseDown('dates')}
                onTouchEnd={() => handleSectionMicrophoneMouseUp('dates')}
              >
                {getMicrophoneIcon(sectionMicStates.dates)}
              </button>
            </div>
            {/* <button type="button" className="settings-icon" onClick={handleOpenDatesSettings}>⚙️</button> */}
          </div>

          {loadingDates ? (
            <div className="section-loading"><div className="section-spinner" /></div>
          ) : (
          <>
          <div className={`form-group ${getConfidenceClass('tradeDate')}`}>
            <label className="form-label">{t('tradeBooking.administrative.tradeDate')} *{renderConfidenceIndicator('tradeDate')}</label>
            <BusinessDayDatePicker
              value={form.tradeDate}
              onChange={(date) => {
                if (date) {
                  updateField('tradeDate', date);
                  setDatesManuallyModified(true);
                }
              }}
              className={errors.tradeDate ? 'error' : ''}
              error={!!errors.tradeDate}
              disabled={isReadOnly}
            />
          </div>

          <div className={`form-group ${getConfidenceClass('valueDate')}`}>
            <label className="form-label">
              {form.tradeType === TradeType.SPOT
                ? t('tradeBooking.spotFields.valueDate')
                : t('tradeBooking.forwardFields.valueDate')} *{renderConfidenceIndicator('valueDate')}
            </label>
            <BusinessDayDatePicker
              value={form.valueDate}
              onChange={(date) => {
                handleValueDateChange(date);
                setDatesManuallyModified(true);
              }}
              className={errors.valueDate ? 'error' : ''}
              error={!!errors.valueDate}
              disabled={isReadOnly}
            />
          </div>

          {form.tradeType === TradeType.FORWARD && (
            <>
              <div className="form-group">
                <label className="form-label">{t('tradeBooking.forwardFields.maturity')} *</label>
                <input
                  type="text"
                  className={`form-input ${errors.maturity ? 'error' : ''}`}
                  value={form.maturity}
                  onChange={(e) => handleMaturityChange(e.target.value)}
                  placeholder={t('tradeBooking.forwardFields.maturityPlaceholder')}
                  disabled={isReadOnly}
                />
              </div>
            </>
          )}

          <div className={`form-group ${getConfidenceClass('expiryDate')}`}>
            <label className="form-label">
              {form.tradeType === TradeType.SPOT
                ? t('tradeBooking.spotFields.expiryDate')
                : t('tradeBooking.forwardFields.expiryDate')} *{renderConfidenceIndicator('expiryDate')}
            </label>
            <BusinessDayDatePicker
              value={form.expiryDate}
              onChange={(date) => {
                handleExpiryDateChange(date);
                setDatesManuallyModified(true);
              }}
              className={errors.expiryDate ? 'error' : ''}
              error={!!errors.expiryDate}
              disabled={isReadOnly}
            />
          </div>

          {/* Payment Date - only shown for Compensación (NDF) */}
          {form.settlementType !== SettlementType.ENTREGA_FISICA && (
            <div className={`form-group ${getConfidenceClass('paymentDate')}`}>
              <label className="form-label">
                {form.tradeType === TradeType.SPOT
                  ? t('tradeBooking.spotFields.paymentDate')
                  : t('tradeBooking.forwardFields.paymentDate')} *{renderConfidenceIndicator('paymentDate')}
              </label>
              <BusinessDayDatePicker
                value={form.paymentDate}
                onChange={(date) => {
                  updateField('paymentDate', date);
                  setDatesManuallyModified(true);
                }}
                className={errors.paymentDate ? 'error' : ''}
                error={!!errors.paymentDate}
                disabled={isReadOnly}
              />
            </div>
          )}

          {/* Valuta dates - shown for Entrega Física (physical delivery) */}
          {form.settlementType === SettlementType.ENTREGA_FISICA && (
            <>
              <div className="form-group">
                <label className="form-label">
                  {t('tradeBooking.dates.currency1PaymentDate')} *
                </label>
                <BusinessDayDatePicker
                  value={form.currency1PaymentDate}
                  onChange={(date) => {
                    updateField('currency1PaymentDate', date);
                  }}
                  className={errors.currency1PaymentDate ? 'error' : ''}
                  error={!!errors.currency1PaymentDate}
                  disabled={isReadOnly}
                />
                {errors.currency1PaymentDate && (
                  <div className="field-error">{t(errors.currency1PaymentDate)}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('tradeBooking.dates.currency2PaymentDate')} *
                </label>
                <BusinessDayDatePicker
                  value={form.currency2PaymentDate}
                  onChange={(date) => {
                    updateField('currency2PaymentDate', date);
                  }}
                  className={errors.currency2PaymentDate ? 'error' : ''}
                  error={!!errors.currency2PaymentDate}
                  disabled={isReadOnly}
                />
                {errors.currency2PaymentDate && (
                  <div className="field-error">{t(errors.currency2PaymentDate)}</div>
                )}
              </div>
            </>
          )}
          </>
          )}
        </div>

        {/* Section 3: Fixing (Forward only) */}
        {form.tradeType === TradeType.FORWARD && (
          <div className="form-section">
            <div className="section-title">
              <div className="section-title-with-mic">
                <span>
                  {form.isAverageRateForward
                    ? t('tradeBooking.forwardAsiatico.sectionTitle')
                    : form.isForwardStarting
                    ? t('tradeBooking.forwardStarting.sectionTitle')
                    : t('tradeBooking.sections.fixing')}
                </span>
                <button
                  type="button"
                  className={`btn-microphone-animated ${sectionMicStates.fixing}`}
                  disabled={isVoiceActive && sectionMicStates.fixing === 'idle'}
                  onMouseDown={() => handleSectionMicrophoneMouseDown('fixing')}
                  onMouseUp={() => handleSectionMicrophoneMouseUp('fixing')}
                  onMouseLeave={() => {
                    if (sectionMicStates.fixing === 'listening') {
                      handleSectionMicrophoneMouseUp('fixing');
                    }
                  }}
                  onTouchStart={() => handleSectionMicrophoneMouseDown('fixing')}
                  onTouchEnd={() => handleSectionMicrophoneMouseUp('fixing')}
                >
                  {getMicrophoneIcon(sectionMicStates.fixing)}
                </button>
              </div>
              {/* <button type="button" className="settings-icon" onClick={() => setShowFixingSettings(true)}>⚙️</button> */}
            </div>

            {loadingFixing ? (
              <div className="section-loading"><div className="section-spinner" /></div>
            ) : (
            <>
            {/* Forward Asiático toggle — hidden when Forward Starting is active */}
            {!form.isForwardStarting && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>
                    {t('tradeBooking.forwardAsiatico.toggle')}
                  </label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={form.isAverageRateForward}
                      onChange={(e) => handleAverageRateForwardToggle(e.target.checked)}
                      disabled={isReadOnly}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            )}

            <div className={`form-group ${getConfidenceClass('settlementType')}`}>
              <label className="form-label">
                {t('tradeBooking.forwardFields.settlementType')} *{renderConfidenceIndicator('settlementType')}
              </label>
              <select
                className={`form-select ${errors.settlementType ? 'error' : ''}`}
                value={form.settlementType}
                onChange={(e) => handleSettlementTypeChange(e.target.value as SettlementType)}
                disabled={isReadOnly || form.isAverageRateForward || form.currency1 === 'CLF'}
              >
                {Object.values(SettlementType)
                  .filter((type) => form.currency1 !== 'CLF' || type === SettlementType.COMPENSACION)
                  .map((type) => (
                    <option key={type} value={type}>
                      {t(`tradeBooking.settlementTypes.${type}`)}
                    </option>
                  ))}
              </select>
            </div>

            <div className={`form-group ${getConfidenceClass('fixingReference')}`}>
              <label className="form-label">{t('tradeBooking.forwardFields.fixingReference')} *{renderConfidenceIndicator('fixingReference')}</label>
              <select
                className={`form-select ${errors.fixingReference ? 'error' : ''}`}
                value={form.fixingReference}
                onChange={(e) => updateField('fixingReference', e.target.value as FixingReference)}
                disabled={isReadOnly || form.settlementType === SettlementType.ENTREGA_FISICA || form.currency1 === 'CLF'}
              >
                {form.settlementType === SettlementType.ENTREGA_FISICA ? (
                  <option value={FixingReference.NA}>N/A</option>
                ) : (
                  Object.values(FixingReference).filter(ref => ref !== FixingReference.NA).map((ref) => (
                    <option key={ref} value={ref}>
                      {t(`tradeBooking.fixingReferences.${ref}`)}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className={`form-group ${getConfidenceClass('fixingOffset')}`}>
              <label className="form-label">{t('tradeBooking.forwardFields.fixingOffset')} *{renderConfidenceIndicator('fixingOffset')}</label>
              <select
                className={`form-select ${errors.fixingOffset ? 'error' : ''}`}
                value={form.fixingOffset}
                onChange={(e) => updateField('fixingOffset', e.target.value as FixingOffset)}
                disabled={isReadOnly || form.isAverageRateForward || form.settlementType === SettlementType.ENTREGA_FISICA}
              >
                {form.settlementType === SettlementType.ENTREGA_FISICA ? (
                  <option value={FixingOffset.NA}>N/A</option>
                ) : (
                  <>
                    <option value={FixingOffset.ZERO}>0</option>
                    <option value={FixingOffset.MINUS_ONE}>-1</option>
                    <option value={FixingOffset.MINUS_TWO}>-2</option>
                  </>
                )}
              </select>
            </div>

            {/* Fixing Schedule Panel — shown when Asiático */}
            {form.isAverageRateForward && (
              <>
                <FixingSchedulePanel
                  schedule={form.averageRateFixingSchedule}
                  tradeDate={form.tradeDate
                    ? `${form.tradeDate.getFullYear()}-${String(form.tradeDate.getMonth() + 1).padStart(2, '0')}-${String(form.tradeDate.getDate()).padStart(2, '0')}`
                    : ''}
                  maturityDate={form.expiryDate
                    ? `${form.expiryDate.getFullYear()}-${String(form.expiryDate.getMonth() + 1).padStart(2, '0')}-${String(form.expiryDate.getDate()).padStart(2, '0')}`
                    : ''}
                  onAddDates={handleFixingScheduleAddDates}
                  onRemoveDate={handleFixingScheduleRemoveDate}
                  onClearAll={handleFixingScheduleClearAll}
                  disabled={isReadOnly}
                />
                {errors.averageRateFixingSchedule && (
                  <span className="error-message">{t(errors.averageRateFixingSchedule)}</span>
                )}
              </>
            )}

            <div className={`form-group ${getConfidenceClass('settlementCurrency')}`}>
              <label className="form-label">{t('tradeBooking.forwardFields.settlementCurrency')} *{renderConfidenceIndicator('settlementCurrency')}</label>
              <select
                className={`form-select ${errors.settlementCurrency ? 'error' : ''}`}
                value={form.settlementCurrency}
                onChange={(e) => updateField('settlementCurrency', e.target.value)}
                disabled={isReadOnly || form.settlementType === SettlementType.ENTREGA_FISICA}
              >
                <option value="">
                  {form.settlementType === SettlementType.ENTREGA_FISICA ? 'N/A' : t('tradeBooking.tradeDetails.selectCurrency')}
                </option>
                {getSettlementCurrencyOptions().map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code}
                  </option>
                ))}
              </select>
            </div>
            </>
            )}
          </div>
        )}

        {/* Section 4: Classification & Notes */}
        <div className="form-section">
          <div className="section-title">
            <div className="section-title-with-mic">
              <span>{t('tradeBooking.sections.classificationAndNotes')}</span>
              <button
                type="button"
                className={`btn-microphone-animated ${sectionMicStates.notes}`}
                disabled={isVoiceActive && sectionMicStates.notes === 'idle'}
                onMouseDown={() => handleSectionMicrophoneMouseDown('notes')}
                onMouseUp={() => handleSectionMicrophoneMouseUp('notes')}
                onMouseLeave={() => {
                  if (sectionMicStates.notes === 'listening') {
                    handleSectionMicrophoneMouseUp('notes');
                  }
                }}
                onTouchStart={() => handleSectionMicrophoneMouseDown('notes')}
                onTouchEnd={() => handleSectionMicrophoneMouseUp('notes')}
              >
                {getMicrophoneIcon(sectionMicStates.notes)}
              </button>
            </div>
          </div>

          {loadingClassification ? (
            <div className="section-loading"><div className="section-spinner" /></div>
          ) : (
          <>
          {/* Grouping 1 & 2 side by side */}
          {groupingsConfig && (
            <div className="form-row">
              <div className={`form-group ${getConfidenceClass('grouping1')}`}>
                <label className="form-label">
                  {groupingsConfig.grouping1Label || t('tradeBooking.classification.grouping1')}{renderConfidenceIndicator('grouping1')}
                </label>
                <select
                  className="form-select"
                  value={form.grouping1}
                  onChange={(e) => {
                    updateField('grouping1', e.target.value);
                    if (groupingsConfig?.grouping2Mode === 'subgrouping') {
                      updateField('grouping2', '');
                      updateField('grouping3', '');
                    } else if (groupingsConfig?.grouping3Mode === 'subgrouping') {
                      updateField('grouping3', '');
                    }
                  }}
                  disabled={isReadOnly || !form.ourPartyId}
                >
                  <option value="">{t('tradeBooking.classification.select')}</option>
                  {getFilteredGroupingValues(1).map(v => (
                    <option key={v.id} value={v.name}>{v.name}</option>
                  ))}
                </select>
              </div>
              {groupingsConfig.grouping2Label && (
                <div className={`form-group ${getConfidenceClass('grouping2')}`}>
                  <label className="form-label">{groupingsConfig.grouping2Label}{renderConfidenceIndicator('grouping2')}</label>
                  <select
                    className="form-select"
                    value={form.grouping2}
                    onChange={(e) => {
                      updateField('grouping2', e.target.value);
                      if (groupingsConfig?.grouping3Mode === 'subgrouping') {
                        updateField('grouping3', '');
                      }
                    }}
                    disabled={isReadOnly || !form.ourPartyId}
                  >
                    <option value="">{t('tradeBooking.classification.select')}</option>
                    {getFilteredGroupingValues(2).map(v => (
                      <option key={v.id} value={v.name}>{v.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {groupingsConfig.grouping3Label && (
                <div className={`form-group ${getConfidenceClass('grouping3')}`}>
                  <label className="form-label">{groupingsConfig.grouping3Label}{renderConfidenceIndicator('grouping3')}</label>
                  <select
                    className="form-select"
                    value={form.grouping3}
                    onChange={(e) => updateField('grouping3', e.target.value)}
                    disabled={isReadOnly || !form.ourPartyId || (groupingsConfig.grouping3Mode === 'subgrouping' && !form.grouping2)}
                  >
                    <option value="">{t('tradeBooking.classification.select')}</option>
                    {getFilteredGroupingValues(3).map(v => (
                      <option key={v.id} value={v.name}>{v.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          <div className={`form-group ${getConfidenceClass('hedgeObject')}`}>
            <label className="form-label">{t('tradeBooking.administrative.hedgeObject')}{renderConfidenceIndicator('hedgeObject')}</label>
            <input
              type="text"
              className={`form-input ${hedgeObjectError ? 'input-error' : ''}`}
              value={form.hedgeObject}
              onChange={(e) => {
                const newValue = e.target.value;
                updateField('hedgeObject', newValue);

                // Validate hedge object on change
                const validation = validateHedgeObject(newValue);
                if (!validation.isValid) {
                  setHedgeObjectError(validation.error || 'Invalid content');
                } else {
                  setHedgeObjectError(null);
                }
              }}
              placeholder={t('tradeBooking.administrative.hedgeObjectPlaceholder')}
              maxLength={50}
              disabled={isReadOnly}
            />
            {hedgeObjectError && (
              <div className="field-error" style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '4px' }}>
                {hedgeObjectError}
              </div>
            )}
          </div>

          <div className={`form-group ${getConfidenceClass('notes')}`}>
            <label className="form-label">{t('tradeBooking.administrative.notes')}{renderConfidenceIndicator('notes')}</label>
            <textarea
              className={`form-textarea ${notesError ? 'input-error' : ''}`}
              value={form.notes}
              onChange={(e) => {
                const newValue = e.target.value;
                updateField('notes', newValue);

                // Validate notes on change
                const validation = validateNotes(newValue);
                if (!validation.isValid) {
                  setNotesError(validation.error || 'Invalid notes content');
                } else {
                  setNotesError(null);
                }
              }}
              placeholder={t('tradeBooking.administrative.notesPlaceholder')}
              rows={3}
              disabled={isReadOnly}
            />
            {notesError && (
              <div className="field-error" style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '4px' }}>
                {notesError}
              </div>
            )}
          </div>

          {/* Allocation Button — commented out, superseded by Multi-Trade splits (KAN-187)
          {groupingsConfig && (
            <div className="form-group">
              <div className="allocation-trigger">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setShowAllocationModal(true)}
                  disabled={isLoading}
                >
                  {t('tradeBooking.allocation.openButton')}
                </button>
                {allocationRows.length > 0 && (() => {
                  const allocTotal = allocationRows.reduce((s, r) => s + (r.amount || 0), 0);
                  const status = form.amount
                    ? Math.abs(allocTotal - form.amount) < 0.01 ? 'balanced' : allocTotal > form.amount ? 'over' : 'under'
                    : 'under';
                  return (
                    <span className={`allocation-badge-inline ${status}`}>
                      {allocationRows.length} {allocationRows.length === 1 ? t('tradeBooking.allocation.row') : t('tradeBooking.allocation.rows')}
                    </span>
                  );
                })()}
              </div>
            </div>
          )}
          */}

          {/* Quote Approval Status Banner (KAN-169) */}
          {isEditable && editingWorkflowState === 'Draft' && editingQuoteApproval?.required && (
            <div className="form-group">
              {editingQuoteApproval.status === 'requested' && (
                <div className="alert alert-info" style={{ margin: '0 0 8px', padding: '8px 12px', fontSize: '0.85rem', borderRadius: '4px', background: '#e8f4fd', border: '1px solid #b6d4e8', color: '#1a5276' }}>
                  {t('tradeBooking.quoteApproval.pendingMessage', 'The approval request has been submitted and is awaiting review.')}
                </div>
              )}
              {editingQuoteApproval.status === 'approved' && (
                <div className="alert alert-success" style={{ margin: '0 0 8px', padding: '8px 12px', fontSize: '0.85rem', borderRadius: '4px', background: '#e8f8e8', border: '1px solid #a3d9a5', color: '#1a5e1a' }}>
                  {t('tradeBooking.quoteApproval.approvedMessage', {
                    defaultValue: 'Approved by {{name}} — this trade may now be booked.',
                    name: editingQuoteApproval.approvedByName || 'supervisor',
                  })}
                </div>
              )}
              {editingQuoteApproval.status === 'rejected' && (
                <div className="alert alert-warning" style={{ margin: '0 0 8px', padding: '8px 12px', fontSize: '0.85rem', borderRadius: '4px', background: '#fff3cd', border: '1px solid #ffc107', color: '#856404' }}>
                  {t('tradeBooking.quoteApproval.rejectedMessage', {
                    defaultValue: 'Request rejected by {{name}}: {{reason}}',
                    name: editingQuoteApproval.rejectedByName || 'supervisor',
                    reason: editingQuoteApproval.rejectionReason || 'No reason provided',
                  })}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="notes-actions">
            {/* Send to Bank mailto — shown for all Forward trades */}
            {form.tradeType === TradeType.FORWARD && (
              <button
                type="button"
                className="fixing-mailto-btn"
                title={t('tradeBooking.forwardAsiatico.sendToBank')}
                onClick={() => {
                  const mailto = generateForwardMailto(
                    form,
                    user?.name || '',
                    form.ourParty || '',
                    locale
                  );
                  window.location.href = mailto;
                }}
              >
                <Mail size={16} />
              </button>
            )}

            {isReadOnly ? (
              /* Confirmed+ trade: view-only, just New Trade */
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleClear}
                disabled={isVoiceActive}
              >
                {t('tradeBooking.actions.newTrade')}
              </button>
            ) : isEditable ? (
              /* Draft/Booked trade: editable, Update + (Book Trade if Draft) + New Trade */
              <>
              <button
                type="button"
                className={editingWorkflowState === 'Draft' ? 'btn btn-outline' : 'btn btn-primary'}
                onClick={handleUpdate}
                disabled={isLoading || isVoiceActive || isReadOnlyRole}
              >
                {isLoading ? t('tradeBooking.messages.loading') : t('tradeBooking.actions.update')}
              </button>
              {editingWorkflowState === 'Draft' && (() => {
                const qaStatus = editingQuoteApproval?.required ? editingQuoteApproval.status : null;
                if (qaStatus === 'pending_request' || qaStatus === 'rejected') {
                  // Needs approval — show "Request Approval" button
                  return (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleRequestQuoteApproval}
                      disabled={isLoading || isVoiceActive || isReadOnlyRole}
                    >
                      {isLoading
                        ? t('tradeBooking.messages.loading')
                        : t('tradeBooking.quoteApproval.requestButton', 'Request Approval')}
                    </button>
                  );
                }
                if (qaStatus === 'requested') {
                  // Waiting for supervisor — show disabled "Approval Pending" button
                  return (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled
                    >
                      {t('tradeBooking.quoteApproval.pendingButton', 'Approval Pending')}
                    </button>
                  );
                }
                // No quote approval required, or status is 'approved' — show normal Book Trade button
                return (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBookDraft}
                    disabled={isLoading || isVoiceActive || isReadOnlyRole}
                  >
                    {isLoading ? t('tradeBooking.messages.loading') : (
                      canDirectlyBook
                        ? t('tradeBooking.actions.bookTrade')
                        : t('tradeBooking.actions.requestApproval')
                    )}
                  </button>
                );
              })()}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClear}
                disabled={isVoiceActive}
              >
                {t('tradeBooking.actions.newTrade')}
              </button>
              </>
            ) : (
              /* New trade: Trade Grid + Clear + Save Draft + Book */
              <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={isLoading || isVoiceActive}
            >
              {t('tradeBooking.actions.clear')}
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={handleSaveDraft}
              disabled={isLoading || isVoiceActive || allocationRows.length > 0 || isReadOnlyRole}
            >
              {t('tradeBooking.actions.saveDraft')}
            </button>

            {/* Show quote-and-book-actions wrapper only when quote functionality is available (demo mode only) */}
            {isDemoMode && form.tradeType === TradeType.SPOT && form.currency1 === 'USD' && form.currency2 === 'CLP' ? (
              <div className="quote-and-book-actions">
                {/* Quote Button - Show for USD/CLP SPOT only */}
                {!activeQuote && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleOpenQuoteModal}
                    disabled={!form.amount || !form.valueDate || isReadOnlyRole}
                  >
                    {t('tradeBooking.quote.requestButton', 'Request Quote')}
                  </button>
                )}

                {/* Quote Timer - Show when quote is active */}
                {activeQuote && (
                  <QuoteTimer
                    dueTimestamp={activeQuote.dueTimestamp}
                    onExpired={handleQuoteExpired}
                  />
                )}

                {/* Book Trade Button */}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isLoading || isVoiceActive || isReadOnlyRole}
                >
                  {isLoading
                    ? t('tradeBooking.messages.loading')
                    : canDirectlyBook
                      ? t('tradeBooking.actions.bookTrade')
                      : t('tradeBooking.actions.requestApproval')
                  }
                </button>
              </div>
            ) : (
              /* No quote functionality - show Book Trade button normally */
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isLoading || isVoiceActive || isReadOnlyRole}
              >
                {isLoading
                  ? t('tradeBooking.messages.loading')
                  : canDirectlyBook
                    ? t('tradeBooking.actions.bookTrade')
                    : t('tradeBooking.actions.requestApproval')
                }
              </button>
            )}
              </>
            )}
            </div>
          </>
          )}
          </div>
          </div>
          </div>
        </Panel>

        <ResizeHandle direction="vertical" />

        <Panel defaultSize={40} minSize={15}>
          <div className={`trade-booking-grid-panel${isExtracting ? ' extracting' : ''}${isVoiceProcessing ? ' voice-processing' : ''}${isLoading ? ' submitting' : ''}`}>
            {dateFrom === null ? null : <ClientTradesGrid
              refreshTrigger={gridRefreshTrigger}
              newlyCreatedTradeId={newlyCreatedTradeId}
              clearAllTrigger={gridClearSelectionTrigger}
              hideUploadControls={true}
              dateFrom={dateFrom || undefined}
              dateTo={dateTo || undefined}
              hideConfirmationColumns={true}
              menuContext="booking"
              onRowSelected={(trade) => {
                // Track which trade is selected for editing
                setEditingTradeId(trade.id || null);
                setEditingTradeClientId(trade.OurPartyId || null);
                setEditingWorkflowState(trade.workflowState || 'Draft');
                setEditingQuoteApproval(trade.quoteApproval || null);

                // Enter view mode
                setIsViewingTrade(true);
                setDatesManuallyModified(true);

                // Populate form with selected trade data
                const tradeType = trade.ProductType === 'Spot' ? 'SPOT' as TradeType : 'FORWARD' as TradeType;
                updateField('tradeType', tradeType);
                updateField('direction', trade.Direction?.toUpperCase() as Direction);
                updateField('amount', trade.QuantityCurrency1 || 0);
                updateField('currency1', trade.Currency1);
                updateField('price', trade.Price);
                updateField('currency2', trade.Currency2);

                // Calculate contra amount
                const amount = trade.QuantityCurrency1 || 0;
                const price = trade.Price || 0;
                updateField('contraAmount', amount * price);

                // Parties — set both ID, display name, and tax ID
                if (trade.OurPartyId) updateField('ourPartyId', trade.OurPartyId);
                if (trade.OurParty) updateField('ourParty', trade.OurParty);
                if (trade.OurPartyTaxId) updateField('ourPartyTaxId', trade.OurPartyTaxId);
                if (trade.CounterpartyId) updateField('counterpartyId', trade.CounterpartyId);
                if (trade.CounterpartyName) updateField('counterparty', trade.CounterpartyName);
                if (trade.CounterpartyTaxId) updateField('counterpartyTaxId', trade.CounterpartyTaxId);

                // Payment methods
                // Spot price / forward points
                updateField('spotPrice', trade.SpotPrice ?? null);
                updateField('forwardPoints', trade.ForwardPoints ?? null);

                // Dates (DD-MM-YYYY or YYYY-MM-DD from backend)
                const tradeDate = parseTradeDate(trade.TradeDate);
                const valueDate = parseTradeDate(trade.ValueDate);
                const paymentDate = parseTradeDate(trade.PaymentDate);
                const maturityDate = parseTradeDate(trade.MaturityDate);
                const currency1PaymentDate = parseTradeDate(trade.Currency1PaymentDate);
                const currency2PaymentDate = parseTradeDate(trade.Currency2PaymentDate);

                if (tradeDate) updateField('tradeDate', tradeDate);
                if (valueDate) updateField('valueDate', valueDate);
                if (paymentDate) updateField('paymentDate', paymentDate);
                if (currency1PaymentDate) updateField('currency1PaymentDate', currency1PaymentDate);
                if (currency2PaymentDate) updateField('currency2PaymentDate', currency2PaymentDate);

                // For forwards, maturityDate is the expiry date
                if (tradeType === 'FORWARD' && maturityDate) {
                  updateField('expiryDate', maturityDate);
                  // Calculate maturity days and formatted string
                  if (valueDate) {
                    const days = calculateDaysDifference(valueDate, maturityDate);
                    updateField('maturityDays', days);
                    updateField('maturity', formatMaturity(days));
                  }
                }

                // Fixing & Settlement — reverse-map Firestore display strings to enum values
                if (trade.FixingReference) {
                  const frMap: Record<string, string> = {
                    'USD Obs': 'USD_OBS', 'UF': 'UF', 'Bloomberg 11 AM': 'BLOOMBERG_11AM', 'N/A': 'N/A',
                  };
                  updateField('fixingReference', frMap[trade.FixingReference] || trade.FixingReference);
                }
                if (trade.FixingOffset !== undefined && trade.FixingOffset !== null) {
                  updateField('fixingOffset', String(trade.FixingOffset));
                }
                if (trade.SettlementType) {
                  // Map Firestore display string back to enum value
                  const stLower = trade.SettlementType.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                  const mappedST = stLower.includes('compensacion')
                    ? SettlementType.COMPENSACION
                    : SettlementType.ENTREGA_FISICA;
                  handleSettlementTypeChange(mappedST);
                }
                if (trade.SettlementCurrency) updateField('settlementCurrency', trade.SettlementCurrency);

                // Average rate forward
                updateField('isAverageRateForward', !!trade.IsAverageRateForward);

                // Forward Starting
                updateField('isForwardStarting', !!trade.IsForwardStarting);
                // KAN-297: Reset price/contraAmount for forward-starting trades so the
                // form state matches the "Por Fijar" display and the backend Price==0 validation passes.
                if (trade.IsForwardStarting) {
                  updateField('price', 0);
                  updateField('contraAmount', 0);
                  setPriceInput('');
                }
                if (trade.IsForwardStarting && trade.SpotFixingReference) {
                  const frMap: Record<string, string> = {
                    'USD Obs': 'USD_OBS', 'UF': 'UF', 'Bloomberg 11 AM': 'BLOOMBERG_11AM', 'N/A': 'N/A',
                  };
                  updateField('spotFixingReference', (frMap[trade.SpotFixingReference] || trade.SpotFixingReference) as FixingReference);
                }
                if (trade.ForwardStartingFixingSchedule && Array.isArray(trade.ForwardStartingFixingSchedule)) {
                  updateField('forwardStartingFixingSchedule', trade.ForwardStartingFixingSchedule.map((e: any) => ({
                    date: e.Date || e.date || '',
                    realisedRate: e.RealisedRate ?? e.realisedRate ?? null,
                    realisedAt: e.RealisedAt ?? e.realisedAt ?? null,
                    realisedBy: e.RealisedBy ?? e.realisedBy ?? null,
                  })));
                }

                // Classification & Notes
                if (trade.Grouping1) updateField('grouping1', trade.Grouping1);
                if (trade.Grouping2) updateField('grouping2', trade.Grouping2);
                if (trade.Grouping3) updateField('grouping3', trade.Grouping3);
                if (trade.HedgeObject) updateField('hedgeObject', trade.HedgeObject);
                updateField('notes', trade.Notes || '');

                // Update formatted inputs for display
                setAmountInput(formatAmountDisplay(trade.QuantityCurrency1 || 0, locale));
                setPriceInput(formatPriceDisplay(trade.Price || 0, locale));
                setSpotPriceInput(trade.SpotPrice != null ? formatPriceDisplay(trade.SpotPrice, locale) : '');
                setForwardPointsInput(trade.ForwardPoints != null ? formatPriceDisplay(trade.ForwardPoints, locale) : '');
              }}
            />}
            {/* Refresh button — below the grid, bottom-right */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px 0' }}>
              <button
                onClick={() => setGridRefreshTrigger(prev => prev + 1)}
                className="btn-secondary btn-small"
                style={{ fontSize: '0.85rem' }}
              >
                {t('common.refresh', 'Refresh')}
              </button>
            </div>
          </div>
        </Panel>
      </PanelGroup>

      {/* Modals */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        form={form}
        actionType={confirmationType}
        locale={locale}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmation(false)}
      />

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />

      <ShortcutsModal
        isOpen={showShortcutsSettings}
        shortcuts={shortcuts}
        editingShortcut={editingShortcut}
        isNewShortcut={isNewShortcut}
        tradingEntities={tradingEntities}
        banks={banks}
        onClose={() => setShowShortcutsSettings(false)}
        onNewShortcut={handleNewShortcut}
        onEditShortcut={handleEditShortcut}
        onDuplicateShortcut={handleDuplicateShortcut}
        onDeleteShortcut={handleDeleteShortcut}
        onSaveShortcut={handleSaveShortcut}
        onCancelEdit={handleCancelShortcutEdit}
        onUpdateEditingShortcut={handleUpdateEditingShortcut}
      />

      <DatesSettingsModal
        isOpen={showDatesSettings}
        conventions={dateConventions}
        onClose={handleCloseDatesSettings}
        onSave={handleSaveDateConventions}
        onResetDefaults={handleResetDateDefaults}
        onAddConvention={handleAddDateConvention}
        onDeleteConvention={handleDeleteDateConvention}
        onToggleOverride={handleToggleOverride}
        onUpdateConvention={handleUpdateDateConvention}
      />

      <FixingExpirySettingsModal
        isOpen={showFixingSettings}
        onClose={() => setShowFixingSettings(false)}
        conventions={fixingExpiryConventions}
        onSave={handleSaveFixingExpiryConventions}
        currencies={currencies}
      />

      <MultiQuoteModal
        isOpen={showQuoteModal}
        tradeData={{
          clientTaxId: user?.organization?.taxId || '',
          currency1: form.currency1,
          currency2: form.currency2,
          direction: form.direction === Direction.BUY ? 'Buy' : 'Sell',
          amount: form.amount || 0,
          currency1SettlementDate: form.valueDate ? form.valueDate.toISOString().split('T')[0] : '',
          currency2SettlementDate: form.valueDate ? form.valueDate.toISOString().split('T')[0] : '',
          product: 'Spot',
        }}
        banks={banks}
        onQuoteSelected={handleQuoteSelected}
        onCancel={() => setShowQuoteModal(false)}
      />

      <AllocationModal
        isOpen={showAllocationModal}
        onClose={() => setShowAllocationModal(false)}
        parentAmount={form.amount}
        tradingEntities={tradingEntities}
        groupingsConfig={groupingsConfig}
        allocationRows={allocationRows}
        onAllocationChange={setAllocationRows}
        disabled={isLoading}
      />

      {/* KAN-216: Pending confirmation match — single match prompt */}
      <ConfirmDialog
        isOpen={!!pendingConfirmationMatch && pendingConfirmationMatch.type === 'single'}
        title={t('tradeBooking.messages.confirmationMatchTitle')}
        message={t('tradeBooking.messages.confirmationMatchSingle', {
          counterparty: pendingConfirmationMatch?.counterpartyName || '',
          date: pendingConfirmationMatch?.receivedDate || '',
          ref: pendingConfirmationMatch?.bankTradeNumber || '',
        })}
        confirmLabel={t('tradeBooking.messages.confirmationReprocessYes')}
        cancelLabel={t('tradeBooking.messages.confirmationReprocessNo')}
        onConfirm={handleReprocessConfirmation}
        onCancel={() => setPendingConfirmationMatch(null)}
        type="info"
      />

      {showFxgoCorrectionsModal && (
        <FxgoCorrectionsModal
          clientId={user?.organization?.id || user?.id || ''}
          isOpen={showFxgoCorrectionsModal}
          onClose={() => {
            setShowFxgoCorrectionsModal(false);
            loadFxgoCorrectionsCount();
          }}
          onRefreshNeeded={() => {
            setGridRefreshTrigger(prev => prev + 1);
            loadFxgoCorrectionsCount();
          }}
        />
      )}

      {/* Multi-trade extraction overlay — spinner with patience text */}
      {isExtracting && !multiTradeModalOpen && (
        <div className="multi-trade-submission-overlay">
          <div className="multi-trade-submission-spinner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="trade-submit-spinner" />
            <p>{t('multiTrade.extracting')}</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>
              {t('multiTrade.extractingPatience')}
            </p>
          </div>
        </div>
      )}

      {multiTradeModalOpen && (
        <MultiTradeModal
          clientId={user?.organization?.id || user?.id || ''}
          extractionResult={multiTradeResult ?? undefined}
          banks={banks}
          tradingEntities={tradingEntities}
          groupingsConfig={groupingsConfig}
          onClose={handleMultiTradeModalClose}
          initialDraftId={selectedDraftId ?? undefined}
        />
      )}

      {/* Draft picker popover — right-click on Layers button (KAN-187e) */}
      {draftPickerOpen && (
        <>
          <div className="draft-picker-backdrop" onClick={handleDraftPickerClose} />
          <div
            className="draft-picker-popover"
            style={{ top: draftPickerPos.y, left: draftPickerPos.x }}
          >
            <div className="draft-picker-header">{t('multiTrade.loadDraft')}</div>
            {draftPickerLoading ? (
              <div className="draft-picker-loading">
                <div className="draft-picker-spinner" />
              </div>
            ) : draftPickerDrafts.length === 0 ? (
              <div className="draft-picker-empty">{t('multiTrade.noDrafts')}</div>
            ) : (
              draftPickerDrafts.map(d => (
                <button
                  key={d.id}
                  className="draft-picker-item"
                  onClick={() => handleDraftPickerSelect(d.id)}
                >
                  <span className="draft-picker-name">{d.name || '—'}</span>
                  <span className="draft-picker-meta">
                    {t('multiTrade.draftRowCount', { count: d.rowCount })}
                  </span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TradeBooking;
